<template>
  <v-container class="chat-wrapper" fluid>
    <!-- Model Select -->
    <!-- <v-row>
      <v-card-title class="model-select">
        <v-select 
          v-model="selectedModel" 
          variant="solo" 
          class="custom-field" 
          :items="models" 
          item-value="name"
          item-title="company" 
          placeholder="Select a Model" 
          density="compact"
          :menu-props="{ disabled: true }"
          :readonly="true"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" class="d-flex align-center">
              <v-avatar left size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </v-list-item>
          </template>

<template #selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="24" class="mr-2">
                <img :src="item.raw.icon" alt="icon" />
              </v-avatar>
              <span>{{ item.raw.name }}</span>
            </div>
          </template>
</v-select>

</v-card-title>
<v-divider class="divider"></v-divider>
</v-row> -->

    <!-- Chat History -->
    <v-row v-if="!getFirstMsg" class="chat-area">
      <v-col cols="12">
        <!-- Responsive height for chat card -->
        <v-card :style="chatCardStyle" class="d-flex flex-column">
          <!-- Chat History Scrollable -->
          <div class="chat-history flex-grow-1" ref="chatHistory" 
               @click="handleChatClick"
               @click.capture="handleChatClickCapture"
               @mousedown="handleMouseDown"
               @mousedown.capture="handleMouseDownCapture"
               @mouseup="handleMouseUp"
               @mouseup.capture="handleMouseUpCapture"
               @mousemove="handleMouseMove"
               @wheel="handleWheel"
               @scroll="handleScroll">
            <!-- Loading indicator for history -->
            <div v-if="historyLoading" class="history-loading">
              <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
              <p class="loading-text">Loading chat history...</p>
            </div>
            <v-list v-else-if="history.length > 0" dense>
              <v-list-item v-for="(msg, index) in history" :key="index">
                <v-list-item-content>
                  <div v-if="msg.role === 'user'" class="profile-Logo">
                    {{ userInitials }}
                  </div>
                  <div v-if="msg.role === 'ai'" class="ai-Logo">
                    <img :src="aiLogo" alt="AI Logo" />
                  </div>
                  <div v-if="msg.role === 'user'" class="right chat-bubble">
                    <div v-html="renderMarkdown(msg.context)"></div>
                  </div>
                  
                  <div v-else class="left chat-bubble" :class="{ 'full-width-studio': msg.studioEmbedded }">
                    <!-- Studio Agent - Plot Link and Prompt -->
                    <div v-if="msg.metadata && (msg.metadata.route === 'StudioAgent')" class="studio-plot-link">
                      <!-- Show prompt if studio not embedded yet -->
                      <div v-if="!msg.studioEmbedded && !msg.studioResponded" class="studio-prompt">
                        <div class="studio-question">
                          <p class="question-text">Do you want to open Studio with this plot in chat?</p>
                          <div class="question-buttons">
                            <v-btn 
                              small 
                              @click="handleStudioYes(index)"
                              class="yes-btn">
                              Yes
                            </v-btn>
                            <v-btn 
                              small 
                              outlined 
                              @click="handleStudioNo(index)"
                              class="no-btn">
                              No
                            </v-btn>
                          </div>
                        </div>
                        <!-- Also show link to open in new page -->
                        <div class="studio-link-container">
                          <a 
                            :href="getStudioPlotUrl(msg.context)" 
                            @click.prevent="openStudioInNewPage(msg.context)"
                            class="studio-link">
                            <v-icon small color="primary" style="margin-right: 8px;">mdi-open-in-new</v-icon>
                            <span>Open in Studio Page</span>
                          </a>
                        </div>
                      </div>
                      <!-- Show embedded studio if user clicked yes -->
                      <div v-else-if="msg.studioEmbedded" class="embedded-studio-container" 
                           @mouseenter="disableScrollPreventionForStudio"
                           @focusin="disableScrollPreventionForStudio"
                           @click.stop="disableScrollPreventionForStudio"
                           @mousedown.stop="disableScrollPreventionForStudio"
                           @touchstart.stop="disableScrollPreventionForStudio"
                           @focus="disableScrollPreventionForStudio">
                       
                        <div class="embedded-studio-content" 
                             @mouseenter="disableScrollPreventionForStudio"
                             @focusin="disableScrollPreventionForStudio"
                             @click.stop="disableScrollPreventionForStudio"
                             @mousedown.stop="disableScrollPreventionForStudio"
                             @focus="disableScrollPreventionForStudio">
                          <StudioData :plotId="getPlotIdFromContext(msg.context)" :embedded="true" />
                        </div>
                        <div class="embedded-studio-footer">
                          <a 
                            :href="getStudioPlotUrl(msg.context)" 
                            @click.prevent.stop="openStudioInNewPage(msg.context)"
                            class="studio-link-footer">
                            <v-icon small color="primary" style="margin-right: 8px;">mdi-open-in-new</v-icon>
                            <span>Open {{ getPlotName(msg.context) }} in Studio Page</span>
                          </a>
                        </div>
                      </div>
                      <!-- Show link if user clicked no -->
                      <div v-else-if="msg.studioResponded && !msg.studioEmbedded" class="studio-link-container">
                        <a 
                          :href="getStudioPlotUrl(msg.context)" 
                          @click.prevent="openStudioInNewPage(msg.context)"
                          class="studio-link">
                          <v-icon small color="primary" style="margin-right: 8px;">mdi-chart-scatter-plot</v-icon>
                          <span>{{ getPlotName(msg.context) }}</span>
                        </a>
                      </div>
                    </div>
                    <!-- Text content -->
                    <div v-else-if="!msg.type || msg.type === 'text'" v-html="renderMarkdown(msg.context)"></div>
                    <!-- Plot content (iframe with blob URL) -->
                    <div v-if="msg.type === 'plot' && msg.plotUrl" class="html-content">
                      <div v-if="msg.plotLoading" class="plot-loading">
                        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                        <p>Loading plot from storage...</p>
                        <p class="plot-loading-hint">This may take a few moments</p>
                      </div>
                      <div v-if="msg.plotError" class="plot-error">
                        <p>Error loading plot. <a :href="msg.plotUrl" target="_blank" rel="noopener noreferrer">Open in new tab</a></p>
                      </div>
                      <iframe 
                      :src="msg.plotUrl" 
                      class="plot-iframe"
                      frameborder="0"
                      scrolling="auto"
                      allow="scripts"
                      referrerpolicy="no-referrer-when-downgrade"
                      @load="handlePlotLoad(index)"
                      @error="handlePlotError(index)"
                      :style="{
                        visibility: (!msg.plotLoading && !msg.plotError) ? 'visible' : 'hidden'
                      }"
                    >
                    </iframe>
                    </div>
                    <!-- HTML content (iframe for plots - legacy format) -->
                    <div v-else-if="msg.type === 'html'" class="html-content">
                      <iframe 
                        :srcdoc="msg.context" 
                        class="plot-iframe"
                        frameborder="0"
                        scrolling="no"
                        sandbox="allow-scripts allow-same-origin"
                        @load="adjustIframeHeight">
                      </iframe>
                    </div>
                    <!-- Visualization Agent - Plot Creation Prompt (shown below HTML chart) -->
                    <div v-if="((msg.metadata && msg.metadata.route === 'visualizationAgent') || (msg.route === 'visualizationAgent')) && !msg.visualizationResponded" 
                         class="visualization-prompt">
                      <div class="visualization-question">
                        <p class="question-text">Do you want to create this plot in studio?</p>
                        <div class="question-buttons">
                          <v-btn 
                            small 
                            @click="handleVisualizationYes(index)"
                            class="yes-btn">
                            Yes
                          </v-btn>
                          <v-btn 
                            small 
                            outlined 
                            @click="handleVisualizationNo(index)"
                            class="no-btn">
                            No
                          </v-btn>
                        </div>
                      </div>
                    </div>
                    <div class="feedback-icons" style="display: flex; gap: 8px; margin-top: 4px;">
                      <img @click="handleFeedback(index, true)" 
                           :src="likeImg" 
                           alt="Thumbs Up"
                           :class="{ 'active-feedback': likeIndex === index }"
                           style="width: 20px; height: 20px; cursor: pointer;" />
                      <img @click="handleFeedback(index, false)" 
                           :src="dislikeImg" 
                           alt="Thumbs Down"
                           :class="{ 'active-feedback': dislikeIndex === index }"
                           style="width: 20px; height: 20px; cursor: pointer;" />
                    </div>
                    
                    <!-- Feedback Form - appears below the message -->
                    <div v-if="dislikeIndex === index" class="feedback-form-below">
                      <div class="feedback-input-container">
                        <textarea v-model="feedbackText"
                          class="feedbackForm w-full bg-gray-100 text-sm text-gray-800 placeholder-gray-500 p-3 rounded-md focus:outline-none resize-none"
                          placeholder="What went wrong" rows="3"></textarea>
                        <button @click="closeFeedback" class="feedback-close-btn">✕</button>
                      </div>
                      <button @click="submitFeedback(index)"
                        class="feedbackButton mt-2 text-sm px-4 py-1.5 rounded-md hover:bg-[#002c3a] transition">
                        Submit Feedback
                      </button>
                    </div>
                  </div>

                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- Input Area -->
          <div class="input-wrapper">
            <div class="inner-chat-input-container" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="inner-file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-text-field v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." hide-details variant="solo" flat rounded
                class="custom-chat-input" @keyup.enter="sendMessage">
                <template #append-inner>
                  <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
                  <img v-else :src="sendImg" alt="Send" class="send-icon" @click="sendMessage" />
                </template>
              </v-text-field>
            </div>
            
            <!-- Hidden file input for inner chat -->
            <input 
              type="file" 
              ref="innerFileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              style="display: none;"
            />
            
            <!-- Helper text when PDF is uploaded -->
            <div v-if="fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>
          </div>
          <!-- Suggestion Buttons - Always visible -->
          <div class="button-row">
            <!-- Buttons when no PDF is uploaded -->
            <template v-if="!isPDFUploaded">
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
            </template>
            
            <!-- Buttons when PDF is uploaded -->
            <template v-else>
              <v-btn outlined small 
                     :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                     :disabled="loading"
                     @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
            </template>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Initial Empty View -->
    <v-row v-else justify="center" align="center" class="fill-height">
      <v-col cols="12" md="auto">
        
        <v-card class="chat-card" elevation="3">
          <div>
            <!-- Loading indicator for history -->
            <div v-if="historyLoading" class="first-screen-loading">
              <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
              <p class="loading-text">Loading chat history...</p>
            </div>
            
            <!-- Title -->
            <h1 v-if="!historyLoading" class="chat-title">Fluids Intelligence Starts Here</h1>
            
            <!-- Initial Input -->
            <div v-if="!historyLoading" class="chat-box" 
                 @dragover="handleDragOver" 
                 @drop="handleDrop"
                 @click="triggerFileUpload" 
                 :class="{ 'drag-over': isDragOver, 'disabled': loading }"
                 @dragenter="isDragOver = true"
                 @dragleave="isDragOver = false">
              <!-- File Upload Icon (Paperclip) -->
              <div class="file-upload-icon" @click.stop="triggerFileUpload" :class="{ 'disabled': loading }">
                <img :src="require('@/assets/Images/FDPal/paperclip-2.svg')" alt="Upload File" />
              </div>
              
              <v-textarea v-model="message" placeholder="Ask a question about fluids characterization data, or drag and drop your PVT pdf file..." rounded flat no-resize
                class="input-box" @click.stop></v-textarea>
              
              <!-- Send Icon (Paper Airplane) -->
              <div class="send-icon-wrapper" @click.stop="sendMessage">
                <v-progress-circular v-if="loading" indeterminate color="primary" size="20"></v-progress-circular>
                <img v-else :src="sendImg" alt="Send" />
              </div>
            </div>

            <!-- Hidden file input -->
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              accept=".pdf" 
              style="display: none;"
            />

            <!-- Helper text when PDF is uploaded -->
            <div v-if="!historyLoading && fileName && falsePDF && !pdfContextUsed" class="pdf-context-helper">
              <small class="text-muted">
                <v-icon small color="info">info</v-icon>
                PDF file uploaded successfully. You can now ask questions about this report.
              </small>
            </div>

            <!-- Button Row -->
            <div v-if="!historyLoading" class="button-row">
              <!-- Buttons when no PDF is uploaded -->
              <template v-if="!isPDFUploaded">
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize data from database for well: (add your well name here)')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize data
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected from database for well: (add your well name). Provide me the details of the samples from this well.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble Point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples from database for well: (add your well name).')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew Point
                </v-btn>
              </template>
              
              <!-- Buttons when PDF is uploaded -->
              <template v-else>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'summarize' }"
                       :disabled="loading"
                       @click="handlePromptClick('summarize', 'Summarize this PVT report.')">
                  <img :src="require('@/assets/Images/FDPal/icons/summarize.svg')" alt="Summarize" />
                  Summarize Report
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'samples' }"
                       :disabled="loading"
                       @click="handlePromptClick('samples', 'How many samples were collected for this study? Provide me the details of the samples in this study.')">
                  <img :src="require('@/assets/Images/FDPal/icons/samples.svg')" alt="# of samples" />
                  No. of samples
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'api' }"
                       :disabled="loading"
                       @click="handlePromptClick('api', 'What is the API of the fluid in this report?')">
                  <img :src="require('@/assets/Images/FDPal/icons/api.svg')" alt="API" />
                  API
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'gor' }"
                       :disabled="loading"
                       @click="handlePromptClick('gor', 'What is the GOR of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="GOR" />
                  GOR
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'bubble' }"
                       :disabled="loading"
                       @click="handlePromptClick('bubble', 'What is the bubble point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Bubble point" />
                  Bubble point
                </v-btn>
                <v-btn outlined small 
                       :class="{ 'active-prompt': activePromptButton === 'dew' }"
                       :disabled="loading"
                       @click="handlePromptClick('dew', 'What is the dew point pressure of the samples in this PVT study?')">
                  <img :src="require('@/assets/Images/FDPal/icons/bubble-point.svg')" alt="Dew point" />
                  Dew point
                </v-btn>
              </template>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarTimeout" :top="snackbarTop">
    {{ snackbarText }}
    <template v-slot:action="{ attrs }">
      <v-btn text v-bind="attrs" @click="snackbar = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import { mapGetters } from 'vuex';
import StudioData from '../layout/FluidsDataStudioTwo/StudioData.vue';
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true
});

export default {
  components: {
    StudioData
  },
  props: {
    uploadedFile: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      selectedModel: 'gpt-4o',
      models: [
        {
          name: 'gpt-4o',
          company: 'OpenAI',
          icon: require('../../assets/Images/FDPal/models/gpt4o.svg'),
        },
        {
          name: 'Llama-3.3-70B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'Phi-4',
          company: 'Microsoft',
          icon: require('../../assets/Images/FDPal/models/phi4.svg'),
        },
        {
          name: 'Meta-Llama-3.1-405B-Instruct',
          company: 'Meta',
          icon: require('../../assets/Images/FDPal/models/llama3.svg'),
        },
        {
          name: 'DeepSeek-R1',
          company: 'DeepSeek',
          icon: require('../../assets/Images/FDPal/models/deepseek.svg'),
        },
      ],
      message: '',
      sendImg: require("../../assets/Images/FDPal/send-2.svg"),
      likeImg: require("../../assets/Images/FDPal/like.svg"),
      dislikeImg: require("../../assets/Images/FDPal/dislike.svg"),
      aiLogo: require("../../assets/Images/FDPal/aiLogo.png"),
      chatContext: '',
      history: [],
      loading: false,
      historyLoading: false, // Separate loading state for history
      snackbar: false,
      snackbarText: 'Ready to help with your fluids analysis questions!',
      snackbarColor: 'error',
      snackbarTimeout: 6000,
      snackbarTop: true,
      windowHeight: window.innerHeight,
      dislikeIndex: null,
      feedbackText: '',
      getFirstMsg: true,
      falsePDF: false,
      isDragOver: false,
      localUploadedFile: null, // Local file reference
      fileName: null, // Store the uploaded file name
      likeIndex: null, // Track the index of the like button
      activePromptButton: null, // Track the active prompt button
      pdfContextUsed: false, // Track if PDF context has been used for first message
      isUploading: false, // Flag to prevent duplicate uploads
      originalScrollTop: 0, // Store original scroll position
      justSentMessage: false, // Flag to track if message was just sent
      scrollPreventionTimeout: null, // Timeout for scroll prevention
      preventScroll: false, // Flag to prevent scrolling
      lastScrollPosition: 0, // Store last scroll position
      isInitialLoad: true, // Flag to track initial page load
      firstClickHandled: false, // Flag to track if first click has been handled
      firstClickPrevented: false, // Flag to permanently prevent first click scroll
      preventFirstScroll: true, // Flag to prevent first scroll after load/send
      scrollPreventionActive: false, // Flag to track if scroll prevention is active
      studioInteractionActive: false, // Flag to track if user is interacting with embedded studio
      studioInteractionTimeout: null, // Timeout for studio interaction flag
      firstClickScrollPreventionHandler: null, // Reference to scroll prevention event handler
      firstClickScrollPreventionInterval: null, // Reference to scroll prevention interval
      firstClickScrollPreventionRAF: null, // Reference to scroll prevention requestAnimationFrame
      isScrollbarInteraction: false, // Flag to track if user is interacting with scrollbar
      scrollbarInteractionTimeout: null, // Timeout to clear scrollbar interaction flag
      autoResetScrollPreventionTimeout: null, // Timeout to automatically reset scroll prevention
      isWheelScrolling: false, // Flag to track if scroll is from mouse wheel
      wheelScrollTimeout: null, // Timeout to clear wheel scroll flag
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser", "getSelectedTenant"]),
    tenantId() {
      // Extract tenant ID from route
      const pathSegments = this.$route.path.split('/').filter(Boolean);
      return pathSegments[0] || '';
    },
    llmUrl() {
      // Check if tenant ID is adnoc_demo tenant ID, use specific URL, otherwise use .env value
      const tenant = this.getSelectedTenant;
      const currentTenantId = tenant?.ID || this.tenantId || localStorage.getItem("tenantId");
      if (currentTenantId === "67f132db0de754aadd3e9552") {
        return "https://adnoc-intelligence.fluidsdata.ai/";
      }
      return process.env.VUE_APP_LLM_URL;
    },
    userInitials() {
      const user = this.getUser;
      if (user && user.firstName && user.lastName) {
        return (
          (user.firstName[0] || '').toUpperCase() +
          (user.lastName[0] || '').toUpperCase()
        );
      }
      return '';
    },
    chatCardStyle() {
      // Responsive height: 
      // - On large screens, use a max height (e.g. 65vh or 700px)
      // - On small screens, use a higher vh
      // This prevents the input from being pushed off the screen on large monitors
      if (this.windowHeight > 900) {
        // Large screens (e.g. 17" 1080p+)
        return {
          height: '85vh',
          maxHeight: '800px',
          minHeight: '400px',
        };
      } else if (this.windowHeight > 700) {
        // Medium screens
        return {
          height: '90vh',
          maxHeight: '90vh',
          minHeight: '350px',
        };
      } else {
        // Small screens
        return {
          height: '75vh',
          maxHeight: '95vh',
          minHeight: '250px',
        };
      }
    },
    isPDFUploaded() {
      return this.fileName !== null && this.falsePDF;
    }
  },
  methods: {
    async handleFeedback(index, isPositive) {
      if (isPositive) {
        // Handle like button
        if (this.likeIndex === index) {
          this.likeIndex = null; // Deselect if already selected
        } else {
          this.likeIndex = index;
          this.dislikeIndex = null; // Deselect dislike if it was selected
        }
        return;
      }

      // Handle dislike button
      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
        this.feedbackText = '';
        return;
      } else {
        this.dislikeIndex = index;
        this.likeIndex = null; // Deselect like if it was selected
        this.feedbackText = '';
      }

      try {
        const response = await axios.post(
          `${this.llmUrl}feedback`,
          {
            message_index: index,
            is_positive: isPositive,
            feedback_text: isPositive ? null : this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
              'X-TenantID': this.tenantId
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        if (isPositive) {
          this.snackbarText = 'Thank you for your positive feedback!';
          this.snackbarColor = 'success';
        } else {
          this.snackbarText = 'Thank you for your feedback!';
          this.snackbarColor = 'success';
        }

        this.snackbar = true;
      } catch (error) {
        console.error("Feedback error:", error);
      } finally {
        if (isPositive) {
          this.dislikeIndex = null;
          this.feedbackText = '';
        }
      }
    },

    async submitFeedback(index) {
      try {
        const response = await axios.post(
          `${this.llmUrl}feedback`,
          {
            message_index: index,
            is_positive: false,
            feedback_text: this.feedbackText
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
              'X-TenantID': this.tenantId
            }
          }
        );

        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }

        // Show success message
        this.snackbarText = 'Thank you for your feedback!';
        this.snackbarColor = 'success';
        this.snackbar = true;

        // Reset feedback form
        this.dislikeIndex = null;
        this.feedbackText = '';

        // Refresh chat history
        await this.getHistory();

      } catch (error) {
        console.error("Feedback error:", error);
        this.snackbarText = 'Error submitting feedback';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },
    toggleDislike(index) {
      if (this.dislikeIndex === index) {
        this.dislikeIndex = null;
      } else {
        this.dislikeIndex = index;
        this.feedbackText = '';
      }
    },
    renderMarkdown(text) {
      return md.render(text || '');
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          // Always scroll to bottom for new messages
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    forceScrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          // Temporarily disable scroll prevention to ensure scroll works
          const wasPreventing = this.preventScroll;
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
          container.scrollTop = container.scrollHeight;
          // Update lastScrollPosition to match the new scroll position
          this.lastScrollPosition = container.scrollHeight;
          
          // Restore preventScroll state if it was enabled (but not during initial load)
          if (!this.isInitialLoad && wasPreventing) {
            setTimeout(() => {
              this.preventScroll = wasPreventing;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(wasPreventing);
              }
            }, 100);
          }
        }
      });
    },
    async uploadPdf() {
      if (this.isUploading) {
        return; // Prevent duplicate uploads
      }
      this.isUploading = true;
      this.loading = true; // Start loading for the upload process
      const formData = new FormData();
      this.falsePDF = false;
      formData.append('file_upload', this.localUploadedFile);

      // Show upload in progress message
      this.snackbarText = 'Upload in progress...';
      this.snackbarColor = 'info';
      this.snackbar = true;

      try {
        const response = await axios.post(`${this.llmUrl}upload-pdf`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });

        if (response.data.status === 401) {
          this.snackbarText = 'Session expired. Please login again.';
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.$router.push('/login');
          return;
        }

        // Extract filename from response - check multiple possible response structures
        let uploadedFileName = null;
        if (response.data) {
          // Try different possible response properties
          uploadedFileName = response.data.response || 
                            response.data.filename || 
                            response.data.file_name || 
                            response.data.fileName ||
                            response.data.name;
        }
        
        // Fallback to the actual file name if response doesn't have it
        if (!uploadedFileName && this.localUploadedFile && this.localUploadedFile.name) {
          uploadedFileName = this.localUploadedFile.name;
        }
        
        // If still no filename, use a default
        if (!uploadedFileName) {
          console.warn('Filename not found in response, using default');
          uploadedFileName = 'uploaded_file.pdf';
        }
        
        this.fileName = uploadedFileName;
        this.falsePDF = true;
        this.pdfContextUsed = false; // Reset flag for new PDF
        
        // Fill the auto-prompt into the input field instead of automatically sending
        const pdfMessage = `Summarize this PVT report pdf: ${this.fileName}`;
        this.message = pdfMessage;
        
        // Show success message
        this.snackbarText = 'File uploaded successfully!';
        this.snackbarColor = 'success';
        this.snackbar = true;
        
        // Don't automatically send the message - let user review and send manually
        this.loading = false; // Reset loading since we're not auto-sending

      } catch (error) {
        this.falsePDF = false;
        console.error("Upload error:", error);
        
        // Show appropriate error message
        if (error.response) {
          this.snackbarText = error.response.data.message || 'Error uploading file';
        } else if (error.request) {
          this.snackbarText = 'No response from server. Please try again.';
        } else {
          this.snackbarText = 'Error uploading file. Please try again.';
        }
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false; // Reset loading on error
      } finally {
        this.isUploading = false; // Reset upload flag
        // Note: loading state is managed by sendMessage method
      }
    },
    async sendMessage() {
      if (this.loading || !this.message.trim()) return;
      if (this.message.trim()) {
        this.getFirstMsg = false;
        // Show loader for messages
        this.loading = true; // Start loading animation
        const userMsg = this.message;
        this.history.push({ 
          role: 'user', 
          context: userMsg,
          metadata: null // User messages don't have metadata
        });
        this.message = '';
        
        // Set flag to prevent unwanted scrolling after message (set BEFORE scrolling)
        this.justSentMessage = true;
        
        // CRITICAL: Clear all existing scroll prevention handlers BEFORE allowing scroll
        const container = this.$refs.chatHistory;
        if (container) {
          // Clear any existing scroll prevention handlers first
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            this.firstClickWheelPreventionHandler = null;
          }
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
        }
        
        // Clear all scroll prevention flags to allow scroll to bottom
        this.preventScroll = false;
        this.preventFirstScroll = false;
        if (this.preventScrollSetter) {
          this.preventScrollSetter(false);
        }
        
        // Force scroll to bottom when user sends a message
        this.forceScrollToBottom();
        
        // Update scroll position after scrolling completes and ENABLE scroll prevention
        // This prevents the initial scroll up that happens before "first click detected"
        this.$nextTick(() => {
          setTimeout(() => {
            const container = this.$refs.chatHistory;
            if (container) {
              this.lastScrollPosition = container.scrollTop;
              // Clear justSentMessage flag after scroll completes
              this.justSentMessage = false;
              
              // CRITICAL: Enable scroll prevention IMMEDIATELY after scroll completes
              // This prevents the initial scroll up that happens before "first click detected"
              console.log('🟡 Message sent - enabling scroll prevention to prevent initial scroll up');
              
              // Enable prevention immediately to prevent any scroll up from initial clicks
              this.firstClickHandled = false; // Allow first-click handler to run if click happens
              this.preventFirstScroll = true; // Enable to prevent initial scroll up
              this.preventScroll = true; // Enable to prevent initial scroll up
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              // Set up immediate scroll prevention to catch any scrolls before "first click detected"
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              // Set up a temporary scroll prevention handler to prevent initial scroll up
              // Only block scrolls that go UP (away from bottom), allow scrolls DOWN (towards bottom)
              const initialScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                // Only prevent if scrolling UP (away from bottom)
                if (newScrollTop < currentScrollTop) {
                  console.log('🔴 Preventing initial scroll up - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  container.scrollTop = currentScrollTop;
                } else {
                  // User is scrolling down - allow it and clear prevention
                  console.log('🟢 User scrolling down - allowing and clearing initial prevention');
                  container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                  this.preventFirstScroll = false;
                  this.preventScroll = false;
                  if (this.preventScrollSetter) {
                    this.preventScrollSetter(false);
                  }
                }
              };
              
              container.addEventListener('scroll', initialScrollPrevention, { passive: false, capture: true });
              
              // Auto-disable prevention after a short time to allow normal scrolling
              setTimeout(() => {
                container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Initial scroll prevention disabled - user can now scroll normally');
              }, 1000); // Disable after 1 second to allow normal scrolling
              
              console.log('🟢 Scroll prevention enabled - will prevent initial scroll up', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll,
                preventScroll: this.preventScroll,
                lastScrollPosition: this.lastScrollPosition
              });
            }
          }, 500); // Wait for scroll to complete
        });
        
        // Reset flags immediately - prevention will be enabled after scroll completes
        this.$nextTick(() => {
          // Reset flags - prevention will be enabled in the setTimeout above
          this.firstClickHandled = false;
          // Don't set preventFirstScroll here - it will be set after scroll completes
        });
        
        // Clear any existing timeouts
        if (this.scrollPreventionTimeout) {
          clearTimeout(this.scrollPreventionTimeout);
        }
        
        // Set timeout to allow scrolling again after 3 seconds
        this.scrollPreventionTimeout = setTimeout(() => {
          this.justSentMessage = false;
        }, 3000);

        // Send question - with or without file
        if (this.localUploadedFile && this.fileName && !this.pdfContextUsed) {
          await this.askQuestion(userMsg);
          // Mark PDF context as used after first message
          this.pdfContextUsed = true;
        } else {
          await this.askQuestion(userMsg);
        }
      }
    },
    async askQuestion(userMsg) {
      // Remove file validation - allow questions without files
      if (this.localUploadedFile && !this.falsePDF) {
        this.loading = false;
        return;
      }
      
      // Prepare the question data with filename as parameter if available
      let questionText = userMsg;
      let questionData = {
        message: questionText
      };
      
      // If there's an uploaded PDF and it's the first time, add filename as parameter
      if (this.fileName && this.falsePDF && !this.pdfContextUsed) {
        questionData.source_pdf = this.fileName;
        // Use the user's message as is, since filename is now a parameter
        questionData.message = userMsg;
      }
      
      try {
        // Keep loading state active during API call
        // this.loading is already true from sendMessage method
        const response = await axios.post(`${this.llmUrl}api/chat`, questionData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });
        if (response.data.status === 401) {
          this.$router.push('/login');
          return;
        }
        // Handle new response format with content and type, or new format with plotUrl
        const responseData = response.data.response;
        
        if (typeof responseData === 'object' && responseData.type) {
          // New format: has type field
          const historyItem = { 
            role: 'ai', 
            context: responseData.content || '', // Content may be empty for plot type
            type: responseData.type,
            metadata: responseData.metadata ? { ...responseData.metadata } : {} // Include metadata if available
          };
          
          // Store plotUrl if available (for visualizationAgent with blob storage)
          if (responseData.plotUrl) {
            historyItem.plotUrl = responseData.plotUrl;
            historyItem.plotLoading = true; // Initialize loading state
            historyItem.plotError = false;
          }
          
          // Store xAxis, yAxis, legend if available (for visualizationAgent)
          if (responseData.xAxis) historyItem.xAxis = responseData.xAxis;
          if (responseData.yAxis) historyItem.yAxis = responseData.yAxis;
          if (responseData.legend) historyItem.legend = responseData.legend;
          
          // Also check if route is at top level and add to metadata
          if (responseData.route) {
            historyItem.route = responseData.route;
            if (!historyItem.metadata.route) {
              historyItem.metadata.route = responseData.route;
            }
          }
          
          // Ensure metadata is always an object (not null)
          if (!historyItem.metadata || typeof historyItem.metadata !== 'object') {
            historyItem.metadata = {};
          }
          
          this.chatContext = historyItem.context || (historyItem.plotUrl ? 'Plot visualization' : '');
          this.history.push(historyItem);
          
          // Set timeout for plot loading (fallback if iframe doesn't trigger load event)
          if (historyItem.plotUrl) {
            const historyIndex = this.history.length - 1;
            setTimeout(() => {
              if (this.history[historyIndex] && this.history[historyIndex].plotLoading) {
                // If still loading after 5 seconds, hide loading state (iframe may have loaded but event didn't fire)
                this.history[historyIndex].plotLoading = false;
              }
            }, 5000);
          }
        } else {
          // Fallback for old format (plain string response)
          this.chatContext = responseData;
          this.history.push({ 
            role: 'ai', 
            context: responseData,
            metadata: null // No metadata in old format
          });
        }
        // Force scroll to bottom when AI response arrives
        this.$nextTick(() => {
          // CRITICAL: Clear all existing scroll prevention handlers BEFORE allowing scroll
          const container = this.$refs.chatHistory;
          if (container) {
            // Clear any existing scroll prevention handlers first
            if (this.firstClickScrollPreventionHandler) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
              this.firstClickScrollPreventionHandler = null;
            }
            if (this.firstClickWheelPreventionHandler) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
              this.firstClickWheelPreventionHandler = null;
            }
            if (this.firstClickScrollPreventionInterval) {
              clearInterval(this.firstClickScrollPreventionInterval);
              this.firstClickScrollPreventionInterval = null;
            }
            if (this.firstClickScrollPreventionRAF !== null) {
              cancelAnimationFrame(this.firstClickScrollPreventionRAF);
              this.firstClickScrollPreventionRAF = null;
            }
          }
          
          // Clear scroll prevention flags to allow scroll to bottom
          this.preventScroll = false;
          this.preventFirstScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
          this.forceScrollToBottom();
          
          // Update scroll position after scrolling completes and ENABLE scroll prevention
          // This prevents the initial scroll up that happens before "first click detected"
          setTimeout(() => {
            const container = this.$refs.chatHistory;
            if (container) {
              this.lastScrollPosition = container.scrollTop;
              // Clear justSentMessage flag
              this.justSentMessage = false;
              
              // CRITICAL: Enable scroll prevention IMMEDIATELY after scroll completes
              // This prevents the initial scroll up that happens before "first click detected"
              console.log('🟡 AI response received - enabling scroll prevention to prevent initial scroll up');
              
              // Enable prevention immediately to prevent any scroll up from initial clicks
              this.firstClickHandled = false; // Allow first-click handler to run if click happens
              this.preventFirstScroll = true; // Enable to prevent initial scroll up
              this.preventScroll = true; // Enable to prevent initial scroll up
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              // Set up immediate scroll prevention to catch any scrolls before "first click detected"
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              // Set up a temporary scroll prevention handler to prevent initial scroll up
              // Only block scrolls that go UP (away from bottom), allow scrolls DOWN (towards bottom)
              const initialScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                // Only prevent if scrolling UP (away from bottom)
                if (newScrollTop < currentScrollTop) {
                  console.log('🔴 Preventing initial scroll up after AI response - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  container.scrollTop = currentScrollTop;
                } else {
                  // User is scrolling down - allow it and clear prevention
                  console.log('🟢 User scrolling down after AI response - allowing and clearing initial prevention');
                  container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                  this.preventFirstScroll = false;
                  this.preventScroll = false;
                  if (this.preventScrollSetter) {
                    this.preventScrollSetter(false);
                  }
                }
              };
              
              container.addEventListener('scroll', initialScrollPrevention, { passive: false, capture: true });
              
              // Auto-disable prevention after a short time to allow normal scrolling
              setTimeout(() => {
                container.removeEventListener('scroll', initialScrollPrevention, { capture: true });
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Initial scroll prevention disabled after AI response - user can now scroll normally');
              }, 1000); // Disable after 1 second to allow normal scrolling
              
              console.log('🟢 Scroll prevention enabled after AI response - will prevent initial scroll up', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll,
                preventScroll: this.preventScroll,
                lastScrollPosition: this.lastScrollPosition
              });
            }
          }, 500); // Wait for scroll to complete
        });
        
        // Reset flags immediately - prevention will be enabled after scroll completes
        this.$nextTick(() => {
          // Reset flags - prevention will be enabled in the setTimeout above
          this.firstClickHandled = false;
          // Don't set preventFirstScroll here - it will be set after scroll completes
        });
      } catch (error) {
        console.error("Answer Question error:", error);
        // Show error message to user
        this.snackbarText = 'Error getting response. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false; // Stop loading animation
      }
    },
    async getHistory() {
      this.historyLoading = true; // Start history loading animation
      // Disable scroll prevention during history load
      this.preventScroll = false;
      this.justSentMessage = true; // Temporarily set to prevent scroll interference
      if (this.preventScrollSetter) {
        this.preventScrollSetter(false);
      }
      try {
        const response = await axios.post(`${this.llmUrl}get-history`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
            'X-TenantID': this.tenantId
          }
        });
        
        // Debug logging
        
        if (response.data.status === 401 || response.data.statusCode === 401) {
          this.$router.push('/login');
          return;
        }
        
        // Handle new response format with history array
        let historyData = [];
        
        // Check if response has history array (new format)
        // Try response.data.data.response (nested structure)
        if (response.data.data && response.data.data.response && Array.isArray(response.data.data.response)) {
          historyData = response.data.data.response;
        }
        // Try response.data.history first
        else if (response.data.history && Array.isArray(response.data.history)) {
          historyData = response.data.history;
        } 
        // Try response.data.response.history
        else if (response.data.response && response.data.response.history && Array.isArray(response.data.response.history)) {
          historyData = response.data.response.history;
        }
        // Check if response.data.response is an array with new format
        else if (response.data.response && Array.isArray(response.data.response)) {
          // Check if it's the new format nested in response
          if (response.data.response[0] && response.data.response[0].content && typeof response.data.response[0].content === 'object') {
            historyData = response.data.response;
          } else {
            // Old format - direct array with role/content
            const lastUserMessage = response.data.response
              .filter(item => item.role === "user")
              .slice(-1)[0];
            this.history = response.data.response.map(item => {
              let content = item.content;
              let type = item.type || 'text'; // Default to text type
              
              if (item.role !== "ai") {
                const regex = /Question:\s*([^]*)/;
                const match = content.match(regex);
                content = match ? match[1] : content;

                if (lastUserMessage) {
                  const pdfRegex = /pdf\s*:\s*(.+?\.pdf)/i;
                  const pdfMatch = content.match(pdfRegex);
                  if (pdfMatch) {
                    this.fileName = pdfMatch[1];
                  }
                }
              }
              return {
                role: item.role,
                context: content,
                type: type,
                metadata: item.metadata || null // Include metadata if available in old format
              };
            });
            // Update getFirstMsg based on whether history exists
            if (this.history.length > 0) {
              this.getFirstMsg = false;
              // Scroll to bottom when history loads
              this.$nextTick(() => {
                this.forceScrollToBottom();
              });
            } else {
            }
            return; // Exit early for old format
          }
        } else {
          // No history available
          this.history = [];
          // Keep getFirstMsg as true to show initial screen
          return;
        }
        
        // Process new format: history array with content object
        this.history = [];
        let lastUserMessage = null;
        
        // If we have history data, we should show the chat screen
        const hasHistoryData = historyData.length > 0;
        
        historyData.forEach(item => {
          const content = item.content || {};
          const userMessage = content.user || '';
          const aiMessage = content.ai || '';
          const mimeType = item.mime_type || 'MemoryMimeType.TEXT';
          const metadata = item.metadata || {}; // Store metadata
          
          // Determine type from mime_type or aiMessage structure
          let type = 'text';
          let plotUrl = null;
          let xAxis = null;
          let yAxis = null;
          let legend = null;
          
          // Check if aiMessage is an object (new format with plotUrl)
          if (typeof aiMessage === 'object' && aiMessage !== null) {
            type = aiMessage.type || 'text';
            plotUrl = aiMessage.plotUrl || null;
            xAxis = aiMessage.xAxis || null;
            yAxis = aiMessage.yAxis || null;
            legend = aiMessage.legend || null;
          } else if (mimeType.includes('HTML') || mimeType.includes('html')) {
            type = 'html';
          }
          
          // Add user message if present
          if (userMessage.trim()) {
            let processedUserMessage = userMessage;
            
            // Extract PDF filename from user message
            const pdfRegex = /pdf\s*:\s*(.+?\.pdf)/i;
            const pdfMatch = processedUserMessage.match(pdfRegex);
            if (pdfMatch) {
              this.fileName = pdfMatch[1];
            }
            
            // Remove "Question:" prefix if present
            const regex = /Question:\s*([^]*)/;
            const match = processedUserMessage.match(regex);
            processedUserMessage = match ? match[1] : processedUserMessage;
            
            this.history.push({
              role: 'user',
              context: processedUserMessage,
              type: 'text',
              metadata: null // User messages don't have metadata
            });
            
            lastUserMessage = processedUserMessage;
          }
          
          // Add AI message if present
          if (aiMessage && (typeof aiMessage === 'string' ? aiMessage.trim() : true)) {
            const historyItem = {
              role: 'ai',
              context: typeof aiMessage === 'string' ? aiMessage : (aiMessage.content || ''),
              type: type,
              metadata: metadata // Store metadata with AI message
            };
            
            // Add plotUrl and visualization data if available
            if (plotUrl) {
              historyItem.plotUrl = plotUrl;
              historyItem.plotLoading = true; // Initialize loading state
              historyItem.plotError = false;
            }
            if (xAxis) {
              historyItem.xAxis = xAxis;
            }
            if (yAxis) {
              historyItem.yAxis = yAxis;
            }
            if (legend) {
              historyItem.legend = legend;
            }
            
            // Only add if there's actual content to display (text or plot)
            if (historyItem.context || historyItem.plotUrl) {
              this.history.push(historyItem);
            }
          }
        });
        
        // Update getFirstMsg based on whether history exists
        // Show chat if we have history data OR processed history items
        if (hasHistoryData || this.history.length > 0) {
          this.getFirstMsg = false;
          // Scroll to bottom when history loads
          this.$nextTick(() => {
            this.forceScrollToBottom();
            // Update lastScrollPosition after a short delay to ensure it's set correctly
            setTimeout(() => {
              const container = this.$refs.chatHistory;
              if (container) {
                this.lastScrollPosition = container.scrollTop;
                // Don't set preventFirstScroll - allow user to scroll freely
              }
            }, 200);
          });
        } else {
        }
      } catch (error) {
        console.error("Get History error:", error);
        this.snackbarText = 'Error loading chat history';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.historyLoading = false; // Stop history loading animation
        // Re-enable normal scroll behavior after a delay to allow rendering and user interaction
        this.$nextTick(() => {
          // Update lastScrollPosition to current position before enabling scroll prevention
          const container = this.$refs.chatHistory;
          if (container) {
            this.lastScrollPosition = container.scrollTop;
          }
          
          setTimeout(() => {
            this.justSentMessage = false;
            // Update lastScrollPosition one more time before clearing isInitialLoad
            if (container) {
              this.lastScrollPosition = container.scrollTop;
            }
            // Wait longer before enabling scroll prevention to ensure user has interacted
            setTimeout(() => {
              this.isInitialLoad = false; // Mark initial load as complete
              // NOW set up scrollTop override after initial load is complete
              this.setupScrollPrevention();
            }, 2000); // Additional delay to ensure everything is settled - increased to 2 seconds
          }, 4000); // Give more time for DOM to settle and user to interact - increased to 4 seconds
        });
      }
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    handleDragOver(event) {
      if (this.loading) return; // Prevent drag and drop during loading
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
    },
    handleDrop(event) {
      if (this.loading) return; // Prevent file drop during loading
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = false; // Reset drag-over state
      const file = event.dataTransfer.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please drop a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
    },
    handleFileSelect(event) {
      if (this.loading) return; // Prevent file selection during loading
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf' && !this.isUploading) {
        this.localUploadedFile = file;
        this.$emit('file-uploaded', file); // Emit to parent
        this.uploadPdf(); // Upload the PDF
      } else if (this.isUploading) {
        this.snackbarText = 'File upload already in progress. Please wait.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      } else {
        this.snackbarText = 'Please select a PDF file.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
      }
      event.target.value = ''; // Clear the input
    },
    triggerFileUpload() {
      if (this.loading) return; // Prevent file upload during loading
      // Try to click the inner file input first, then fall back to the initial one
      if (this.$refs.innerFileInput) {
        this.$refs.innerFileInput.click();
      } else if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    closeFeedback() {
      this.dislikeIndex = null;
      this.feedbackText = '';
    },
    handlePromptClick(type, text) {
      this.activePromptButton = type;
      
      // If there's a PDF uploaded, append the filename to the prompt text
      if (this.fileName && this.falsePDF) {
        this.message = `${text} pdf: ${this.fileName}`;
      } else {
        // No PDF, just use the prompt text
        this.message = text;
      }
    },
    clearPDFState() {
      // Clear all PDF-related state
      this.localUploadedFile = null;
      this.fileName = null;
      this.falsePDF = false;
      this.pdfContextUsed = false;
      this.message = '';
      this.activePromptButton = null;
    },
    setupScrollPrevention() {
      // DISABLED: ScrollTop setter override causes more problems than it solves
      // We'll rely on handleScroll to track position only, not restore it
      // This prevents unwanted scroll restoration on user clicks
      this.$nextTick(() => {
        const container = this.$refs.chatHistory;
        if (container) {
          // Store original scroll position
          this.originalScrollTop = container.scrollTop;
          
          // NO scrollTop setter override - allow all scrolling
          // We only track position in handleScroll, never restore it
          this.preventScrollSetter = (prevent) => {
            // Do nothing - we're not preventing scrolls anymore
          };
        }
      });
    },
    handleDocumentClick(event) {
      // Document-level click handler - EARLIEST possible handler
      // Check if click is within chat history container
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const target = event.target;
      const isClickInChatHistory = container.contains(target) || container === target;
      
      if (isClickInChatHistory) {
        // Check if clicking on scrollbar FIRST - if so, disable scroll prevention immediately
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer for better detection
        
        if (isScrollbarClick) {
          // Immediately disable all scroll prevention for scrollbar interaction
          console.log('🔵 SCROLLBAR CLICK DETECTED in handleDocumentClick - disabling all scroll prevention');
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          // Clear ALL scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          // Clear timeout if exists
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          // Keep flag active for a longer time to allow scrollbar drag
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow the click to proceed normally - don't block scrollbar
        }
        
        // Check if clicking on embedded studio - if so, disable scroll prevention immediately
        const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                       target.closest('.embedded-studio-content') ||
                                       target.closest('.embedded-studio-footer');
        
        if (isClickOnEmbeddedStudio) {
          // Immediately disable all scroll prevention for embedded studio
         
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.preventFirstScroll = false;
          this.studioInteractionActive = true;
          this.preventScroll = false;
          
          // Clear scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          // Also call the disable function to ensure everything is cleared
          this.disableScrollPreventionForStudio();
          
          return; // Allow the click to proceed normally
        }
        
        // Log first click in chat box
        // Note: Scroll prevention should already be set up in handleDocumentMouseDown (earlier in event chain)
        // If it wasn't set up in mousedown, set it up here as fallback
        if (!this.firstClickHandled && !this.isScrollbarInteraction) {
          console.log('🔵 First click detected on empty space - setting up scroll prevention', {
            firstClickHandled: this.firstClickHandled,
            preventFirstScroll: this.preventFirstScroll
          });
          this.firstClickHandled = true;
          
          // DISABLE SCROLL on first click - prevent any scroll changes
          const container = this.$refs.chatHistory;
          if (container) {
            // Store current scroll position IMMEDIATELY and lock it
            const currentScrollTop = container.scrollTop;
            this.lastScrollPosition = currentScrollTop;
            
            // CRITICAL: Lock scroll position IMMEDIATELY before any handlers are set up
            // This prevents any scroll that might happen during the click event
            container.scrollTop = currentScrollTop;
            
            // Enable scroll prevention flags when click happens
            this.preventFirstScroll = true;
            this.preventScroll = true;
            
            // If scroll prevention setter exists, use it
            if (this.preventScrollSetter) {
              this.preventScrollSetter(true);
            }
            
            // Store scroll prevention handler reference for cleanup
            this.firstClickScrollPreventionHandler = null;
            this.firstClickScrollPreventionInterval = null;
            this.firstClickScrollPreventionRAF = null;
            this.firstClickWheelPreventionHandler = null;
            
            // Create a scroll prevention handler that restores scroll position IMMEDIATELY
            // BUT ONLY for programmatic scrolls, NOT user-initiated scrolls (mouse wheel, scrollbar)
            const preventScrollHandler = (e) => {
              // ALWAYS check for embedded studio FIRST - before any prevention
              // Check multiple ways to detect embedded studio interaction
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              const scrollTarget = e.target;
              const isScrollOnEmbeddedStudio = scrollTarget && (
                scrollTarget.closest('.embedded-studio-container') ||
                scrollTarget.closest('.embedded-studio-content')
              );
              
              // Check if container has embedded studio elements
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              // Check mouse position to see if it's over embedded studio
              const mouseEvent = e;
              let isMouseOverEmbeddedStudio = false;
              if (mouseEvent && mouseEvent.clientX && mouseEvent.clientY) {
                const elementAtPoint = document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY);
                isMouseOverEmbeddedStudio = elementAtPoint && (
                  elementAtPoint.closest('.embedded-studio-container') ||
                  elementAtPoint.closest('.embedded-studio-content')
                );
              }
              
              // Don't prevent scroll if ANY indication of embedded studio interaction
              if (this.studioInteractionActive || isInEmbeddedStudio || isScrollOnEmbeddedStudio || hasEmbeddedStudio || isMouseOverEmbeddedStudio) {
                // Disable prevention immediately and permanently
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                // Remove this handler immediately
                container.removeEventListener('scroll', preventScrollHandler, { capture: true });
                this.firstClickScrollPreventionHandler = null;
                return; // Allow scrolling during studio interaction
              }
              
              // CRITICAL: Always allow scrollbar interactions - never block them
              if (this.isScrollbarInteraction) {
                // User is dragging scrollbar - allow all scrolling
                return;
              }
              
              // CRITICAL: On first click, BLOCK ALL scrolls immediately - prevent any scroll changes
              // This prevents the random scroll up issue on first click
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                // IMMEDIATELY restore scroll position - block ALL scrolls during first click prevention
                console.log('🔴 BLOCKING scroll on first click - restoring to', currentScrollTop, 'from', container.scrollTop);
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                container.scrollTop = currentScrollTop;
                return false;
              }
            };
            
            // Store handler reference
            this.firstClickScrollPreventionHandler = preventScrollHandler;
            
            // Add scroll event listener to prevent scrolling (capture phase, non-passive)
            container.addEventListener('scroll', preventScrollHandler, { passive: false, capture: true });
            
            // Also prevent wheel events during first click to block mouse wheel scrolling
            const preventWheelHandler = (e) => {
              if (this.preventFirstScroll && !this.studioInteractionActive) {
                // Check for embedded studio
                const activeElement = document.activeElement;
                const isInEmbeddedStudio = activeElement && (
                  activeElement.closest('.embedded-studio-container') ||
                  activeElement.closest('.embedded-studio-content')
                );
                
                if (!isInEmbeddedStudio) {
                  console.log('🔴 BLOCKING wheel scroll on first click');
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
              }
            };
            this.firstClickWheelPreventionHandler = preventWheelHandler;
            container.addEventListener('wheel', preventWheelHandler, { passive: false, capture: true });
            
            // Use requestAnimationFrame loop to constantly restore scroll position
            // This is more reliable than trying to override scrollTop property
            const restoreScrollLoop = () => {
              // ALWAYS check for embedded studio FIRST - before any prevention
              // Check multiple ways to detect embedded studio interaction
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              // Check if container has embedded studio elements
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              // Don't prevent scroll if ANY indication of embedded studio interaction
              if (this.studioInteractionActive || isInEmbeddedStudio || hasEmbeddedStudio) {
                // Disable prevention immediately and stop the loop
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                this.firstClickScrollPreventionRAF = null;
                return; // Stop the loop if studio interaction is active
              }
              
              // CRITICAL: Always allow scrollbar interactions - never block them
              if (this.isScrollbarInteraction) {
                // User is dragging scrollbar - allow all scrolling
                return;
              }
              
              // CRITICAL: On first click, BLOCK ALL scrolls immediately - prevent any scroll changes
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                // IMMEDIATELY restore scroll position - block ALL scrolls during first click prevention
                container.scrollTop = currentScrollTop;
              }
              
              if (this.preventFirstScroll && !this.studioInteractionActive && !isInEmbeddedStudio && !hasEmbeddedStudio) {
                this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
              } else {
                // Stop the loop
                this.firstClickScrollPreventionRAF = null;
              }
            };
            this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
            
            // Also use interval as backup (in case RAF doesn't catch everything)
            const scrollPreventionInterval = setInterval(() => {
              // ALWAYS check for embedded studio FIRST - before any prevention
              // Check multiple ways to detect embedded studio interaction
              const activeElement = document.activeElement;
              const isInEmbeddedStudio = activeElement && (
                activeElement.closest('.embedded-studio-container') ||
                activeElement.closest('.embedded-studio-content')
              );
              
              // Check if container has embedded studio elements
              const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
              
              // Don't prevent scroll if ANY indication of embedded studio interaction
              if (this.studioInteractionActive || isInEmbeddedStudio || hasEmbeddedStudio) {
                // Disable prevention immediately and clear interval
                this.preventFirstScroll = false;
                this.studioInteractionActive = true;
                clearInterval(scrollPreventionInterval);
                this.firstClickScrollPreventionInterval = null;
                return; // Stop preventing if studio interaction is active
              }
              
              // CRITICAL: Always allow scrollbar interactions - never block them
              if (this.isScrollbarInteraction) {
                // User is dragging scrollbar - allow all scrolling
                return;
              }
              
              // CRITICAL: On first click, BLOCK ALL scrolls immediately - prevent any scroll changes
              if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                // IMMEDIATELY restore scroll position - block ALL scrolls during first click prevention
                container.scrollTop = currentScrollTop;
              }
            }, 1); // Check every 1ms for maximum responsiveness
            
            // Store interval reference
            this.firstClickScrollPreventionInterval = scrollPreventionInterval;
            
            // Clear the interval, RAF, and remove listener after a delay
            setTimeout(() => {
              if (this.firstClickScrollPreventionInterval) {
                clearInterval(this.firstClickScrollPreventionInterval);
                this.firstClickScrollPreventionInterval = null;
              }
              if (this.firstClickScrollPreventionRAF !== null) {
                cancelAnimationFrame(this.firstClickScrollPreventionRAF);
                this.firstClickScrollPreventionRAF = null;
              }
              if (this.firstClickScrollPreventionHandler) {
                container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
                this.firstClickScrollPreventionHandler = null;
              }
              if (this.firstClickWheelPreventionHandler) {
                container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
                this.firstClickWheelPreventionHandler = null;
              }
              
              // Keep preventFirstScroll enabled for a bit longer to prevent delayed scrolls
              setTimeout(() => {
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Scroll prevention disabled after first click - normal scrolling now allowed');
              }, 500); // Increased delay to catch more delayed scrolls
            }, 800); // Increased delay before cleanup
            
          }
        }
        
        // Check if clicking on empty area (not on message/interactive element)
        const isClickOnMessage = target.closest('.chat-bubble') || 
                                 target.closest('.v-list-item') ||
                                 target.closest('.v-list-item-content') ||
                                 target.closest('.studio-plot-link') ||
                                 target.closest('.visualization-prompt') ||
                                 target.closest('.html-content') ||
                                 target.closest('.embedded-studio-container') ||
                                 target.closest('.embedded-studio-content') ||
                                 target.closest('.embedded-studio-footer') ||
                                 target.closest('a') ||
                                 target.closest('button') ||
                                 target.closest('img') ||
                                 target.closest('input') ||
                                 target.closest('select') ||
                                 target.closest('textarea') ||
                                 target.closest('.v-select') ||
                                 target.closest('.v-text-field') ||
                                 target.closest('.v-btn');
        
        // If clicking on empty area (not on message/interactive element)
        if (!isClickOnMessage) {
          // Check if it's scrollbar click
          const rect = container.getBoundingClientRect();
          const scrollbarWidth = container.offsetWidth - container.clientWidth;
          const clickX = event.clientX - rect.left;
          const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
          
          if (!isScrollbarClick) {
            // Check if clicking directly on container or v-list background
            const isDirectContainerClick = target === container || 
                                           (target.classList && target.classList.contains('chat-history')) ||
                                           (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
            
            if (isDirectContainerClick) {
              // BLOCK THE CLICK - prevent it from propagating to any other handlers
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              return false;
            }
          }
        }
      }
    },
    handleDocumentMouseDown(event) {
      // Document-level mousedown handler - EARLIEST possible handler
      // Check if click is within chat history container
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const target = event.target;
      const isClickInChatHistory = container.contains(target) || container === target;
      
      if (isClickInChatHistory) {
        // Check if clicking on scrollbar FIRST - if so, disable scroll prevention immediately
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer for better detection
        
        if (isScrollbarClick) {
          // Immediately disable all scroll prevention for scrollbar interaction
          console.log('🔵 SCROLLBAR MOUSEDOWN DETECTED in handleDocumentMouseDown - disabling all scroll prevention');
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          // Clear ALL scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          // Clear timeout if exists
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          // Keep flag active for a longer time to allow scrollbar drag
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow the mousedown to proceed normally - don't block scrollbar
        }
        
        // Check if clicking on embedded studio - if so, disable scroll prevention immediately
        const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                       target.closest('.embedded-studio-content') ||
                                       target.closest('.embedded-studio-footer');
        
        if (isClickOnEmbeddedStudio) {
          // Immediately disable all scroll prevention for embedded studio
         
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.preventFirstScroll = false;
          this.studioInteractionActive = true;
          this.preventScroll = false;
          
          // Clear scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          // Also call the disable function to ensure everything is cleared
          this.disableScrollPreventionForStudio();
          
          return; // Allow the mousedown to proceed normally
        }
        
        // CRITICAL: Set up scroll prevention on MOUSEDOWN (before click/scroll can occur)
        // This prevents the scroll up that happens before "first click detected" console log
        if (!this.firstClickHandled && !this.isScrollbarInteraction) {
          // Check if clicking on empty area (not on message/interactive element)
          const isClickOnMessage = target.closest('.chat-bubble') ||
                                   target.closest('.v-list-item') ||
                                   target.closest('.v-list-item-content') ||
                                   target.closest('.studio-plot-link') ||
                                   target.closest('.visualization-prompt') ||
                                   target.closest('.html-content') ||
                                   target.closest('.embedded-studio-container') ||
                                   target.closest('.embedded-studio-content') ||
                                   target.closest('.embedded-studio-footer') ||
                                   target.closest('a') ||
                                   target.closest('button') ||
                                   target.closest('img') ||
                                   target.closest('input') ||
                                   target.closest('select') ||
                                   target.closest('textarea') ||
                                   target.closest('.v-select') ||
                                   target.closest('.v-text-field') ||
                                   target.closest('.v-btn');
          
          // If clicking on empty area, set up scroll prevention IMMEDIATELY on mousedown
          if (!isClickOnMessage) {
            const rect = container.getBoundingClientRect();
            const scrollbarWidth = container.offsetWidth - container.clientWidth;
            const clickX = event.clientX - rect.left;
            const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
            
            if (!isScrollbarClick) {
              console.log('🔵 MOUSEDOWN detected on empty space - setting up scroll prevention IMMEDIATELY', {
                firstClickHandled: this.firstClickHandled,
                preventFirstScroll: this.preventFirstScroll
              });
              
              // Mark as handled to prevent duplicate setup
              this.firstClickHandled = true;
              
              // Store current scroll position IMMEDIATELY and lock it
              const currentScrollTop = container.scrollTop;
              this.lastScrollPosition = currentScrollTop;
              
              // CRITICAL: Lock scroll position IMMEDIATELY on mousedown (before any scroll can occur)
              container.scrollTop = currentScrollTop;
              
              // Enable scroll prevention flags IMMEDIATELY
              this.preventFirstScroll = true;
              this.preventScroll = true;
              
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
              
              // Set up immediate scroll prevention handler to catch any scrolls
              const immediateScrollPrevention = (e) => {
                const newScrollTop = container.scrollTop;
                // Block ALL scrolls that change position from the locked position
                if (newScrollTop !== currentScrollTop) {
                  console.log('🔴 MOUSEDOWN: Preventing scroll - restoring to', currentScrollTop, 'from', newScrollTop);
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  // Force restore immediately
                  container.scrollTop = currentScrollTop;
                  return false;
                }
              };
              
              // Add handler immediately with highest priority (capture phase, non-passive)
              container.addEventListener('scroll', immediateScrollPrevention, { passive: false, capture: true });
              
              // Store reference for cleanup
              this.firstClickScrollPreventionHandler = immediateScrollPrevention;
              
              // Also prevent wheel events that might cause scroll during click
              // But allow wheel scrolling if user is already using wheel
              const preventWheelDuringClick = (e) => {
                // Don't block if user is actively wheel scrolling
                if (this.isWheelScrolling) {
                  return; // Allow wheel scrolling
                }
                if (this.preventFirstScroll) {
                  console.log('🔴 MOUSEDOWN: Blocking wheel event to prevent scroll during click');
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
              };
              container.addEventListener('wheel', preventWheelDuringClick, { passive: false, capture: true });
              this.firstClickWheelPreventionHandler = preventWheelDuringClick;
              
              // Also set up RAF loop for immediate prevention
              const restoreScrollLoop = () => {
                if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                  container.scrollTop = currentScrollTop;
                }
                if (this.preventFirstScroll) {
                  this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
                } else {
                  this.firstClickScrollPreventionRAF = null;
                }
              };
              this.firstClickScrollPreventionRAF = requestAnimationFrame(restoreScrollLoop);
              
              // Set up interval as backup
              this.firstClickScrollPreventionInterval = setInterval(() => {
                if (this.preventFirstScroll && container.scrollTop !== currentScrollTop) {
                  container.scrollTop = currentScrollTop;
                }
              }, 1);
              
              // Auto-disable after delay
              setTimeout(() => {
                if (this.firstClickScrollPreventionHandler === immediateScrollPrevention) {
                  container.removeEventListener('scroll', immediateScrollPrevention, { capture: true });
                  this.firstClickScrollPreventionHandler = null;
                }
                if (this.firstClickWheelPreventionHandler === preventWheelDuringClick) {
                  container.removeEventListener('wheel', preventWheelDuringClick, { capture: true });
                  this.firstClickWheelPreventionHandler = null;
                }
                if (this.firstClickScrollPreventionInterval) {
                  clearInterval(this.firstClickScrollPreventionInterval);
                  this.firstClickScrollPreventionInterval = null;
                }
                if (this.firstClickScrollPreventionRAF !== null) {
                  cancelAnimationFrame(this.firstClickScrollPreventionRAF);
                  this.firstClickScrollPreventionRAF = null;
                }
                this.preventFirstScroll = false;
                this.preventScroll = false;
                if (this.preventScrollSetter) {
                  this.preventScrollSetter(false);
                }
                console.log('🟢 Scroll prevention disabled after mousedown - normal scrolling now allowed');
              }, 800);
            }
          }
        }
        
        // Check if clicking on empty area (not on message/interactive element)
        const isClickOnMessage = target.closest('.chat-bubble') || 
                                 target.closest('.v-list-item') ||
                                 target.closest('.v-list-item-content') ||
                                 target.closest('.studio-plot-link') ||
                                 target.closest('.visualization-prompt') ||
                                 target.closest('.html-content') ||
                                 target.closest('.embedded-studio-container') ||
                                 target.closest('.embedded-studio-content') ||
                                 target.closest('.embedded-studio-footer') ||
                                 target.closest('a') ||
                                 target.closest('button') ||
                                 target.closest('img') ||
                                 target.closest('input') ||
                                 target.closest('select') ||
                                 target.closest('textarea') ||
                                 target.closest('.v-select') ||
                                 target.closest('.v-text-field') ||
                                 target.closest('.v-btn');
        
        // If clicking on empty area (not on message/interactive element)
        if (!isClickOnMessage) {
          // Check if it's scrollbar click
          const rect = container.getBoundingClientRect();
          const scrollbarWidth = container.offsetWidth - container.clientWidth;
          const clickX = event.clientX - rect.left;
          const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
          
          if (!isScrollbarClick) {
            // Check if clicking directly on container or v-list background
            const isDirectContainerClick = target === container || 
                                           (target.classList && target.classList.contains('chat-history')) ||
                                           (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
            
            if (isDirectContainerClick) {
              // BLOCK THE CLICK - prevent it from propagating to any other handlers
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              return false;
            }
          }
        }
      }
    },
    handleNativeClick(event) {
      // Log first click in chat box (earliest point)
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      // Native event listener in capture phase - most aggressive blocking
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      // Check if clicking directly on the container
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth - 5; // Add 5px buffer
        
        if (isScrollbarClick) {
          // Immediately disable all scroll prevention for scrollbar interaction
          console.log('🔵 SCROLLBAR MOUSEDOWN DETECTED in handleNativeMouseDown - disabling all scroll prevention');
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          // Clear ALL scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          // Clear timeout if exists
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          // Keep flag active for a longer time to allow scrollbar drag
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
          
          return; // Allow scrollbar mousedown - don't block it
        }
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      // Check if clicking on v-list background (not on a list item)
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleNativeMouseDown(event) {
      // Native event listener in capture phase - most aggressive blocking
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      // Check if clicking on embedded studio FIRST - if so, disable scroll prevention immediately
      const isClickOnEmbeddedStudio = target.closest('.embedded-studio-container') || 
                                     target.closest('.embedded-studio-content') ||
                                     target.closest('.embedded-studio-footer');
      
      if (isClickOnEmbeddedStudio) {
        // Immediately disable all scroll prevention for embedded studio
        
        
        // CRITICAL: Set flags FIRST before clearing mechanisms
        this.preventFirstScroll = false;
        this.studioInteractionActive = true;
        this.preventScroll = false;
        
        // Clear scroll prevention mechanisms IMMEDIATELY
        if (this.firstClickScrollPreventionInterval) {
          clearInterval(this.firstClickScrollPreventionInterval);
          this.firstClickScrollPreventionInterval = null;
        }
        if (this.firstClickScrollPreventionRAF !== null) {
          cancelAnimationFrame(this.firstClickScrollPreventionRAF);
          this.firstClickScrollPreventionRAF = null;
        }
        if (this.firstClickScrollPreventionHandler) {
          container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
          this.firstClickScrollPreventionHandler = null;
        }
        if (this.firstClickWheelPreventionHandler) {
          container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
          this.firstClickWheelPreventionHandler = null;
        }
        
        // Also call the disable function to ensure everything is cleared
        this.disableScrollPreventionForStudio();
        
        return; // Allow the mousedown to proceed normally
      }
      
      // Check if clicking directly on the container
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      // Check if clicking on v-list background (not on a list item)
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleNativeMouseUp(event) {
      // Native event listener in capture phase - most aggressive blocking
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) return;
      
      // Check if clicking directly on the container
      if (target === container || (target.classList && target.classList.contains('chat-history'))) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
      
      // Check if clicking on v-list background (not on a list item)
      if (target.classList && target.classList.contains('v-list')) {
        const isOnListItem = target.closest('.v-list-item');
        if (!isOnListItem) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleChatClickCapture(event) {
      // Log first click in chat box (capture phase - runs early)
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      // Capture phase - check if click is on empty area before it bubbles
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      // Check if clicking directly on the container, v-list background, or empty space
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleChatClick(event) {
      // Log first click in chat box
      if (!this.firstClickHandled) {
        this.firstClickHandled = true;
      }
      
      // Check if user clicked on a message/bubble - allow it
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      // If clicking on a message or interactive element, allow it for text selection
      if (isClickOnMessage) {
        // Disable scroll prevention when clicking on embedded studio
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow the click for text selection
      }
      
      // Check if user is clicking on scrollbar - allow it
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      // If clicking on scrollbar, allow it and enable scrollbar interaction
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        // Clear timeout if exists
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        // Keep flag active for a bit to allow scrollbar drag
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar clicks
      }
      
      // PERMANENTLY prevent all clicks on empty chat area
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseDownCapture(event) {
      // Capture phase - check if mousedown is on empty area before it bubbles
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      // Check if clicking directly on the container, v-list background, or empty space
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleMouseDown(event) {
      // Check if user clicked on a message/bubble - allow it for text selection
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      // If clicking on a message or interactive element, allow it
      if (isClickOnMessage) {
        // Disable scroll prevention when clicking on embedded studio
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow mousedown for text selection
      }
      
      // Check if user is clicking on scrollbar - allow it
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      // If clicking on scrollbar, allow it and enable scrollbar interaction
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        // Clear timeout if exists
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        // Keep flag active for a bit to allow scrollbar drag
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar mousedown
      }
      
      // PERMANENTLY prevent all mousedown on empty chat area
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseUpCapture(event) {
      // Capture phase - check if mouseup is on empty area before it bubbles
      const target = event.target;
      const container = this.$refs.chatHistory;
      
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      
      // Check if clicking directly on the container, v-list background, or empty space
      const isDirectContainerClick = target === container || 
                                     (target.classList && target.classList.contains('chat-history')) ||
                                     (target.classList && target.classList.contains('v-list') && !target.closest('.v-list-item'));
      
      if (isDirectContainerClick) {
        // Check if it's scrollbar click
        const rect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const clickX = event.clientX - rect.left;
        const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
        
        if (!isScrollbarClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    },
    handleMouseUp(event) {
      // Check if user clicked on a message/bubble - allow it for text selection
      const target = event.target;
      const isClickOnMessage = target.closest('.chat-bubble') || 
                               target.closest('.v-list-item') ||
                               target.closest('.v-list-item-content') ||
                               target.closest('.studio-plot-link') ||
                               target.closest('.visualization-prompt') ||
                               target.closest('.html-content') ||
                               target.closest('.embedded-studio-container') ||
                               target.closest('.embedded-studio-content') ||
                               target.closest('.embedded-studio-footer') ||
                               target.closest('a') ||
                               target.closest('button') ||
                               target.closest('img') ||
                               target.closest('input') ||
                               target.closest('select') ||
                               target.closest('textarea') ||
                               target.closest('.v-select') ||
                               target.closest('.v-text-field') ||
                               target.closest('.v-btn');
      
      // If clicking on a message or interactive element, allow it
      if (isClickOnMessage) {
        // Disable scroll prevention when clicking on embedded studio
        if (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content')) {
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return; // Allow mouseup for text selection
      }
      
      // Check if user is clicking on scrollbar - allow it and enable scrollbar interaction
      const container = this.$refs.chatHistory;
      if (!container) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const clickX = event.clientX - rect.left;
      const isScrollbarClick = clickX > container.clientWidth - scrollbarWidth;
      
      // If clicking on scrollbar, allow it and enable scrollbar interaction
      if (isScrollbarClick) {
        this.isScrollbarInteraction = true;
        this.preventFirstScroll = false;
        // Clear timeout if exists
        if (this.scrollbarInteractionTimeout) {
          clearTimeout(this.scrollbarInteractionTimeout);
        }
        // Keep flag active for a bit to allow scrollbar drag
        this.scrollbarInteractionTimeout = setTimeout(() => {
          this.isScrollbarInteraction = false;
        }, 2000);
        return; // Allow scrollbar mouseup
      }
      
      // PERMANENTLY prevent all mouseup on empty chat area
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    },
    handleMouseMove(event) {
      // Detect if mouse is over scrollbar area and change cursor
      const container = this.$refs.chatHistory;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const scrollbarWidth = container.offsetWidth - container.clientWidth;
      const mouseX = event.clientX - rect.left;
      const isOverScrollbar = mouseX > container.clientWidth - scrollbarWidth;
      
      // Change cursor style based on scrollbar hover
      if (isOverScrollbar) {
        container.style.cursor = 'pointer';
        // If mouse button is pressed and over scrollbar, user is dragging scrollbar
        if (event.buttons === 1) {
          console.log('🔵 SCROLLBAR DRAG DETECTED in handleMouseMove - ensuring scroll prevention is disabled');
          
          // CRITICAL: Set flags FIRST before clearing mechanisms
          this.isScrollbarInteraction = true;
          this.preventFirstScroll = false;
          this.preventScroll = false;
          this.studioInteractionActive = false;
          
          // Clear ALL scroll prevention mechanisms IMMEDIATELY
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            this.firstClickScrollPreventionHandler = null;
          }
          
          // Clear timeout if exists
          if (this.scrollbarInteractionTimeout) {
            clearTimeout(this.scrollbarInteractionTimeout);
          }
          // Keep flag active while dragging
          this.scrollbarInteractionTimeout = setTimeout(() => {
            this.isScrollbarInteraction = false;
          }, 3000);
        }
      } else {
        container.style.cursor = 'text';
      }
    },
    handleWheel(event) {
      // Mark that user is scrolling with mouse wheel
      // This allows wheel scrolling to work immediately without blocking
      this.isWheelScrolling = true;
      
      // Clear any existing timeout
      if (this.wheelScrollTimeout) {
        clearTimeout(this.wheelScrollTimeout);
      }
      
      // Clear the flag after a short delay (wheel scrolling has stopped)
      this.wheelScrollTimeout = setTimeout(() => {
        this.isWheelScrolling = false;
      }, 150); // Clear after 150ms of no wheel events
      
      // Clear scroll prevention when user scrolls with wheel
      if (this.preventFirstScroll || this.preventScroll) {
        this.preventFirstScroll = false;
        this.preventScroll = false;
        if (this.preventScrollSetter) {
          this.preventScrollSetter(false);
        }
      }
    },
    handleScroll(event) {
      // ALWAYS check for embedded studio interaction FIRST - before any scroll prevention
      const container = this.$refs.chatHistory;
      if (container) {
        // Check if scroll is happening due to embedded studio interaction
        const activeElement = document.activeElement;
        const isInEmbeddedStudio = activeElement && (
          activeElement.closest('.embedded-studio-container') ||
          activeElement.closest('.embedded-studio-content')
        );
        
        // Also check if any element in the container is an embedded studio
        const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
        const mouseTarget = event.target;
        const isClickOnEmbeddedStudio = mouseTarget && (
          mouseTarget.closest('.embedded-studio-container') ||
          mouseTarget.closest('.embedded-studio-content')
        );
        
        if (isInEmbeddedStudio || isClickOnEmbeddedStudio || hasEmbeddedStudio) {
          // Allow scrolling for embedded studio - disable all prevention
          if (this.preventFirstScroll || this.studioInteractionActive === false) {
            this.disableScrollPreventionForStudio();
          }
          // Update scroll position tracking but don't prevent
          this.lastScrollPosition = container.scrollTop;
          return;
        }
      }
      
      // CRITICAL: Always allow scrollbar interactions - never block them
      if (this.isScrollbarInteraction) {
        // User is dragging scrollbar - allow all scrolling
        if (container) {
          this.lastScrollPosition = container.scrollTop;
        }
        return;
      }
      
      // CRITICAL: Always allow mouse wheel scrolling - never block it
      if (this.isWheelScrolling) {
        // User is scrolling with mouse wheel - allow all scrolling
        if (container) {
          this.lastScrollPosition = container.scrollTop;
        }
        // Clear prevention when wheel scrolling
        if (this.preventFirstScroll || this.preventScroll) {
          this.preventFirstScroll = false;
          this.preventScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
        }
        return;
      }
      
      // Allow all user-initiated scrolls to proceed normally
      // The scroll prevention mechanisms (interval/RAF) will handle restoring position after clicks if needed
      // This ensures mouse wheel, scrollbar, and touch scrolling work immediately without blocking
      
      // Update scroll position tracking
      const currentScroll = container ? container.scrollTop : (event.target ? event.target.scrollTop : 0);
      
      // Check if embedded studio exists and user is interacting
      if (container) {
        const hasEmbeddedStudio = container.querySelector('.embedded-studio-container');
        if (hasEmbeddedStudio) {
          const activeElement = document.activeElement;
          const isInEmbeddedStudio = activeElement && (
            activeElement.closest('.embedded-studio-container') ||
            activeElement.closest('.embedded-studio-content')
          );
          
          if (isInEmbeddedStudio || this.studioInteractionActive) {
            // Studio interaction - disable all prevention
            this.lastScrollPosition = container.scrollTop;
            this.preventScroll = false;
            this.preventFirstScroll = false;
            if (this.preventScrollSetter) {
              this.preventScrollSetter(false);
            }
            return;
          }
        }
      }
      
      // Store previous scroll position before updating
      const previousScrollPosition = this.lastScrollPosition;
      
      // Update the last scroll position
      this.lastScrollPosition = currentScroll;
      
      // If user successfully scrolled (not blocked and position changed)
      // CRITICAL: Only clear prevention for DOWNWARD scrolls (user scrolling down is intentional)
      // Keep prevention active for UPWARD scrolls if we're in first-click prevention mode
      // This prevents clearing prevention right before a click, which would allow the scroll-up
      if (container && !this.isScrollbarInteraction && !this.studioInteractionActive && previousScrollPosition !== undefined) {
        const scrollDelta = currentScroll - previousScrollPosition;
        const absDelta = Math.abs(scrollDelta);
        
        // If scroll position actually changed
        if (absDelta > 1) {
          // Clear the auto-reset timeout since user is already scrolling
          if (this.autoResetScrollPreventionTimeout) {
            clearTimeout(this.autoResetScrollPreventionTimeout);
            this.autoResetScrollPreventionTimeout = null;
          }
          
          // CRITICAL: Only clear prevention for DOWNWARD scrolls (towards bottom)
          // Keep prevention active for UPWARD scrolls if we haven't handled first click yet
          // This prevents the scroll-up that happens on first click
          if (scrollDelta > 0) {
            // Scrolling DOWN (towards bottom) - user-initiated, clear prevention
            if (this.preventFirstScroll || this.preventScroll) {
              console.log('🟢 User scrolled DOWN - clearing scroll prevention immediately');
              this.preventFirstScroll = false;
              this.preventScroll = false;
              // Don't clear firstClickHandled - user might still click after scrolling
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            }
          } else {
            // Scrolling UP (away from bottom)
            // CRITICAL: Only block click-triggered upward scrolls, allow wheel scrolling
            if (this.isWheelScrolling) {
              // User is scrolling with wheel - allow it
              console.log('🟢 User scrolled UP with wheel - allowing');
              this.preventFirstScroll = false;
              this.preventScroll = false;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            } else if (!this.firstClickHandled) {
              // Scrolling UP without wheel - might be from click, block it
              console.log('🔴 User scrolled UP (not wheel) before first click handled - blocking and restoring position');
              // Restore to previous position to prevent scroll-up from click
              if (container) {
                container.scrollTop = previousScrollPosition;
                this.lastScrollPosition = previousScrollPosition;
              }
              // Keep prevention active
              this.preventFirstScroll = true;
              this.preventScroll = true;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(true);
              }
            } else if (this.firstClickHandled && (this.preventFirstScroll || this.preventScroll)) {
              // First click already handled - allow upward scroll
              console.log('🟢 User scrolled UP after first click handled - clearing scroll prevention');
              this.preventFirstScroll = false;
              this.preventScroll = false;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            }
          }
        }
      }
    },
    adjustIframeHeight(event) {
      // Auto-adjust iframe height based on content
      const iframe = event.target;
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDoc) {
          const height = iframeDoc.body.scrollHeight;
          if (height > 0) {
            iframe.style.height = Math.min(height + 20, 600) + 'px'; // Max height of 600px
          }
        }
      } catch (error) {
        // If cross-origin restrictions prevent access, set a default height
        iframe.style.height = '400px';
      }
    },
    handlePlotLoad(index) {
      // Mark plot as loaded successfully
      if (this.history[index]) {
        this.history[index].plotLoading = false;
        this.history[index].plotError = false;
        // Try to adjust height and check if content is accessible
        this.$nextTick(() => {
          const chatHistory = this.$refs.chatHistory;
          if (chatHistory) {
            const iframes = chatHistory.querySelectorAll('iframe');
            const targetIframe = Array.from(iframes).find(iframe => 
              iframe.src === this.history[index].plotUrl || 
              iframe.src.includes(this.history[index].plotUrl)
            );
            if (targetIframe) {
              // Check if we can access the iframe content (CORS check)
              try {
                const iframeDoc = targetIframe.contentDocument || targetIframe.contentWindow.document;
                if (iframeDoc && iframeDoc.body) {
                  // Content is accessible, adjust height
                  this.adjustIframeHeight({ target: targetIframe });
                } else {
                  // CORS might be blocking, but iframe loaded - set default height
                  targetIframe.style.height = '400px';
                }
              } catch (error) {
                // CORS error - iframe loaded but content not accessible
                // This is expected for cross-origin blob storage
                // Set a reasonable default height
                targetIframe.style.height = '400px';
              }
            }
          }
        });
      }
    },
    handlePlotError(index) {
      // Mark plot as failed to load
      if (this.history[index]) {
        this.history[index].plotLoading = false;
        this.history[index].plotError = true;
      }
    },
    getPlotName(content) {
      // Parse StudioAgent content to extract plot name
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.api_payload && contentObj.api_payload.name) {
          return contentObj.api_payload.name;
        }
        return 'View Plot';
      } catch (error) {
        console.error('Error parsing plot name:', error);
        return 'View Plot';
      }
    },
    getStudioPlotUrl(content) {
      // Generate URL for studio with plot
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.plotId) {
          return `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
        }
        return `/${this.tenantId}/Fluidsdata-studio`;
      } catch (error) {
        console.error('Error generating studio URL:', error);
        return `/${this.tenantId}/Fluidsdata-studio`;
      }
    },
    openStudioWithPlot(content) {
      // Navigate to FluidsData Studio in the same window with the plot
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        
        if (contentObj && contentObj.plotId && contentObj.api_payload) {
          // Store plot data in localStorage for the studio to pick up
          const plotData = {
            ID: contentObj.plotId,
            Name: contentObj.api_payload.name,
            ChartType: contentObj.api_payload.ChartType,
            TableType: contentObj.api_payload.TableType,
            XAxisColumn: contentObj.api_payload.XAxisColumn,
            YAxisColumns: contentObj.api_payload.YAxisColumns,
            ZAxisColumn: contentObj.api_payload.ZAxisColumn,
            GroupColumn: contentObj.api_payload.GroupColumn,
          };
          
          // Store in localStorage
          localStorage.setItem('savedChartData', JSON.stringify(plotData));
          
          // Navigate to studio in the same window
          const url = `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
          this.$router.push(url);
        } else {
          // Fallback: just navigate to studio without plot
          const url = `/${this.tenantId}/Fluidsdata-studio`;
          this.$router.push(url);
        }
      } catch (error) {
        console.error('Error opening studio with plot:', error);
        // Fallback: just navigate to studio
        const url = `/${this.tenantId}/Fluidsdata-studio`;
        this.$router.push(url);
      }
    },
    hasVisualizationData(msg) {
      // Check if message has visualization data (xaxis, yaxis, legend)
      // Debug logging
      // Check top-level msg properties first (from API response structure)
      if (msg.xAxis && msg.yAxis && msg.legend) {
        return true;
      }
      
      // Check metadata
      if (msg.metadata) {
        const xAxis = msg.metadata.xaxis || msg.metadata.xAxis || msg.metadata.XAxis;
        const yAxis = msg.metadata.yaxis || msg.metadata.yAxis || msg.metadata.YAxis;
        const legend = msg.metadata.legend || msg.metadata.Legend;
        if (xAxis && yAxis && legend) {
          return true;
        }
      }
      
      // Check content if it's JSON
      try {
        const content = typeof msg.context === 'string' ? JSON.parse(msg.context) : msg.context;
        if (content && (content.xaxis || content.xAxis || content.XAxis) && 
            (content.yaxis || content.yAxis || content.YAxis) && 
            (content.legend || content.Legend)) {
          return true;
        }
      } catch (error) {
        // Not JSON, that's okay
      }
      
      return false;
    },
    getVisualizationData(msg) {
      // Extract xaxis, yaxis, and legend from message
      // Priority: metadata > top-level msg properties > content
      
      // Check metadata first
      if (msg.metadata) {
        const xaxis = this.message.xaxis || msg.xAxis || msg.XAxis;
        const yaxis = msg.yaxis || msg.yAxis || msg.YAxis;
        const legend = msg.legend || msg.Legend;
        if (xaxis && yaxis && legend) {
          return { xaxis, yaxis, legend };
        }
      }
      
      // Check top-level msg properties (from API response structure)
      if (msg.xAxis && msg.yAxis && msg.legend) {
        return {
          xaxis: msg.xAxis,
          yaxis: msg.yAxis,
          legend: msg.legend
        };
      }
      
      // Check content if it's JSON
      try {
        const content = typeof msg.context === 'string' ? JSON.parse(msg.context) : msg.context;
        if (content) {
          const xaxis = content.xaxis || content.xAxis || content.XAxis;
          const yaxis = content.yaxis || content.yAxis || content.YAxis;
          const legend = content.legend || content.Legend;
          if (xaxis && yaxis && legend) {
            return { xaxis, yaxis, legend };
          }
        }
      } catch (error) {
        // Not JSON, that's okay
      }
      
      return { xaxis: '', yaxis: '', legend: '' };
    },
    handleVisualizationYes(index) {
      // Handle Yes click - send auto-prompt to create plot
      const msg = this.history[index];
      const vizData = this.getVisualizationData(msg);
      
      // Mark this message as responded to (Vue 3 - direct assignment works)
      this.history[index].visualizationResponded = true;
       const prompt = `Yes, create the scatter plot for Cross test, for ${vizData.yaxis} VS ${vizData.xaxis} with respect to ${vizData.legend} in the studio.`;
      
      // Set the message and send it
      this.message = prompt;
      this.sendMessage();
    },
    handleVisualizationNo(index) {
      // Handle No click - send simple "No thanks"
      // Mark this message as responded to (Vue 3 - direct assignment works)
      this.history[index].visualizationResponded = true;
    },
    handleStudioYes(index) {
      // Handle Yes click - embed studio in chat
      const msg = this.history[index];
      if (msg) {
        // Mark this message as responded to and embedded
        // Vue 3 handles reactivity automatically for direct assignments
        this.history[index].studioResponded = true;
        this.history[index].studioEmbedded = true;
        
        // Immediately disable scroll prevention when studio is embedded
        this.$nextTick(() => {
          // Clear all scroll prevention mechanisms
          if (this.firstClickScrollPreventionInterval) {
            clearInterval(this.firstClickScrollPreventionInterval);
            this.firstClickScrollPreventionInterval = null;
          }
          if (this.firstClickScrollPreventionRAF !== null) {
            cancelAnimationFrame(this.firstClickScrollPreventionRAF);
            this.firstClickScrollPreventionRAF = null;
          }
          if (this.firstClickScrollPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
            }
            this.firstClickScrollPreventionHandler = null;
          }
          if (this.firstClickWheelPreventionHandler) {
            const container = this.$refs.chatHistory;
            if (container) {
              container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
            }
            this.firstClickWheelPreventionHandler = null;
          }
          
          this.studioInteractionActive = true;
          this.preventScroll = false;
          this.preventFirstScroll = false;
          if (this.preventScrollSetter) {
            this.preventScrollSetter(false);
          }
          
        });
      }
    },
    handleStudioNo(index) {
      // Handle No click - just show link
      const msg = this.history[index];
      if (msg) {
        // Mark this message as responded to but not embedded
        this.history[index].studioResponded = true;
        this.history[index].studioEmbedded = false;
      }
    },
    closeEmbeddedStudio(index) {
      // Close embedded studio
      const msg = this.history[index];
      if (msg) {
        this.history[index].studioEmbedded = false;
      }
    },
    getPlotIdFromContext(content) {
      // Extract plotId from content
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        if (contentObj && contentObj.plotId) {
          return contentObj.plotId;
        }
        return null;
      } catch (error) {
        console.error('Error parsing plot ID:', error);
        return null;
      }
    },
    openStudioInNewPage(content) {
      // Navigate to FluidsData Studio in a new page with the plot
      try {
        const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
        
        if (contentObj && contentObj.plotId && contentObj.api_payload) {
          // Store plot data in localStorage for the studio to pick up
          const plotData = {
            ID: contentObj.plotId,
            Name: contentObj.api_payload.name,
            ChartType: contentObj.api_payload.ChartType,
            TableType: contentObj.api_payload.TableType,
            XAxisColumn: contentObj.api_payload.XAxisColumn,
            YAxisColumns: contentObj.api_payload.YAxisColumns,
            ZAxisColumn: contentObj.api_payload.ZAxisColumn,
            GroupColumn: contentObj.api_payload.GroupColumn,
          };
          
          // Store in localStorage
          localStorage.setItem('savedChartData', JSON.stringify(plotData));
          
          // Navigate to studio in the same window
          const url = `/${this.tenantId}/Fluidsdata-studio?plotId=${contentObj.plotId}`;
          this.$router.push(url);
        } else {
          // Fallback: just navigate to studio without plot
          const url = `/${this.tenantId}/Fluidsdata-studio`;
          this.$router.push(url);
        }
      } catch (error) {
        console.error('Error opening studio in new page:', error);
        // Fallback: just navigate to studio
        const url = `/${this.tenantId}/Fluidsdata-studio`;
        this.$router.push(url);
      }
    },
    disableScrollPreventionForStudio() {
      // Disable all scroll prevention when user interacts with embedded studio
      const container = this.$refs.chatHistory;
      
      if (container) {
        // CRITICAL: Store and LOCK current scroll position BEFORE disabling prevention
        const currentScrollTop = container.scrollTop;
        this.lastScrollPosition = currentScrollTop;
        
        // Immediately prevent any scroll changes by locking the position
        // Use requestAnimationFrame to ensure we catch any pending scrolls
        requestAnimationFrame(() => {
          if (container && container.scrollTop !== currentScrollTop) {
            container.scrollTop = currentScrollTop;
          }
        });
        
        // Also use a one-time scroll handler to prevent immediate scrolls
        const lockScrollHandler = (e) => {
          if (container.scrollTop !== currentScrollTop) {
            e.preventDefault();
            e.stopPropagation();
            container.scrollTop = currentScrollTop;
            // Remove handler after one use
            container.removeEventListener('scroll', lockScrollHandler, { capture: true });
          }
        };
        container.addEventListener('scroll', lockScrollHandler, { passive: false, capture: true, once: true });
      }
      
      // CRITICAL: Set flags FIRST before clearing mechanisms (so handlers see the flags immediately)
      this.studioInteractionActive = true;
      this.preventScroll = false;
      this.preventFirstScroll = false; // CRITICAL: Disable first click scroll prevention
      
      if (this.preventScrollSetter) {
        this.preventScrollSetter(false);
      }
      
      // Clear first click scroll prevention mechanisms IMMEDIATELY
      if (this.firstClickScrollPreventionInterval) {
        clearInterval(this.firstClickScrollPreventionInterval);
        this.firstClickScrollPreventionInterval = null;
      }
      if (this.firstClickScrollPreventionRAF !== null) {
        cancelAnimationFrame(this.firstClickScrollPreventionRAF);
        this.firstClickScrollPreventionRAF = null;
      }
      if (this.firstClickScrollPreventionHandler) {
        if (container) {
          container.removeEventListener('scroll', this.firstClickScrollPreventionHandler, { capture: true });
        }
        this.firstClickScrollPreventionHandler = null;
      }
      if (this.firstClickWheelPreventionHandler) {
        if (container) {
          container.removeEventListener('wheel', this.firstClickWheelPreventionHandler, { capture: true });
        }
        this.firstClickWheelPreventionHandler = null;
      }
      
      
      // Keep the flag active for a longer period to catch delayed scroll events
      // Clear any existing timeout
      if (this.studioInteractionTimeout) {
        clearTimeout(this.studioInteractionTimeout);
      }
      
      this.studioInteractionTimeout = setTimeout(() => {
        // Check if still interacting with studio
        const container = this.$refs.chatHistory;
        if (container) {
          const activeElement = document.activeElement;
          const isInEmbeddedStudio = activeElement && (
            activeElement.closest('.embedded-studio-container') ||
            activeElement.closest('.embedded-studio-content')
          );
          if (!isInEmbeddedStudio) {
            this.studioInteractionActive = false;
          } else {
            // Still interacting, extend the timeout
            this.studioInteractionTimeout = setTimeout(() => {
              this.studioInteractionActive = false;
            }, 2000);
          }
        } else {
          this.studioInteractionActive = false;
        }
      }, 2000);
    },
    handleGlobalFocus(event) {
      // Prevent scroll when focusing on elements inside embedded studio
      const target = event.target;
      if (target && (target.closest('.embedded-studio-container') || target.closest('.embedded-studio-content'))) {
        // Disable scroll prevention and prevent scrollIntoView
        this.disableScrollPreventionForStudio();
        
        // Prevent default scrollIntoView behavior
        if (target.scrollIntoView) {
          const originalScrollIntoView = target.scrollIntoView;
          target.scrollIntoView = function() {
            // Do nothing - prevent scroll
          };
          // Restore after a short delay
          setTimeout(() => {
            target.scrollIntoView = originalScrollIntoView;
          }, 100);
        }
      }
    },
  },
  watch: {
    uploadedFile(newFile) {
      if (newFile && !this.isUploading) {
        this.localUploadedFile = newFile; // Update local file reference
        // Don't call uploadPdf here since it's already handled by handleFileSelect/handleDrop
        // Just update the local reference
      } else if (newFile === "" || newFile === null) {
        // PDF was removed, reset internal state
        this.clearPDFState();
      }
    },
    message(newMessage) {
      // No need to enforce PDF context restoration since filename is now passed as parameter
      // The user can freely edit the message
    },
    history: {
      handler(newHistory, oldHistory) {
        // Scroll to bottom when new messages are added
        if (newHistory && newHistory.length > 0) {
          // Only scroll if a new message was added (not on initial load)
          // During initial load, getHistory() will handle scrolling
          if (!this.isInitialLoad && (!oldHistory || newHistory.length > oldHistory.length)) {
            this.$nextTick(() => {
              this.forceScrollToBottom();
            });
          }
          
          // Check if any message has embedded studio - disable scroll prevention
          const hasEmbeddedStudio = newHistory.some(msg => msg.studioEmbedded === true);
          if (hasEmbeddedStudio) {
            this.$nextTick(() => {
              // Permanently disable scroll prevention when studio is embedded
              this.studioInteractionActive = true;
              this.preventScroll = false;
              this.preventFirstScroll = false;
              if (this.preventScrollSetter) {
                this.preventScrollSetter(false);
              }
            });
          }
        }
      },
      deep: true
    }
  },
  mounted() {
    this.getHistory(); // Call getHistory on mount
    window.addEventListener('resize', this.handleResize);
    
    // Add global focus handler to prevent scroll when focusing on embedded studio elements
    document.addEventListener('focusin', this.handleGlobalFocus, true);
    
    // Add document-level click listener in capture phase - EARLIEST possible handler
    // This catches clicks before any other handlers, including Vue handlers
    document.addEventListener('click', this.handleDocumentClick, true);
    document.addEventListener('mousedown', this.handleDocumentMouseDown, true);
    
    // Add native click listener in capture phase to block empty area clicks
    this.$nextTick(() => {
      const chatHistoryEl = this.$refs.chatHistory;
      if (chatHistoryEl) {
        chatHistoryEl.addEventListener('click', this.handleNativeClick, true);
        chatHistoryEl.addEventListener('mousedown', this.handleNativeMouseDown, true);
        chatHistoryEl.addEventListener('mouseup', this.handleNativeMouseUp, true);
      }
    });
    
    // DON'T set up scrollTop override during initial load - wait until initial load is complete
    // This prevents any interference with scrolling during page load
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('focusin', this.handleGlobalFocus, true);
    
    // Remove document-level event listeners
    document.removeEventListener('click', this.handleDocumentClick, true);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
    
    // Remove native event listeners
    const chatHistoryEl = this.$refs.chatHistory;
    if (chatHistoryEl) {
      chatHistoryEl.removeEventListener('click', this.handleNativeClick, true);
      chatHistoryEl.removeEventListener('mousedown', this.handleNativeMouseDown, true);
      chatHistoryEl.removeEventListener('mouseup', this.handleNativeMouseUp, true);
    }
    
    // Clear any pending timeouts
    if (this.selectionTimeout) {
      clearTimeout(this.selectionTimeout);
    }
    if (this.scrollPreventionTimeout) {
      clearTimeout(this.scrollPreventionTimeout);
    }
    if (this.autoResetScrollPreventionTimeout) {
      clearTimeout(this.autoResetScrollPreventionTimeout);
    }
    if (this.studioInteractionTimeout) {
      clearTimeout(this.studioInteractionTimeout);
    }
    if (this.scrollbarInteractionTimeout) {
      clearTimeout(this.scrollbarInteractionTimeout);
    }
    if (this.wheelScrollTimeout) {
      clearTimeout(this.wheelScrollTimeout);
    }
  }
};
</script>

<style scoped>
.chat-wrapper {
  padding-top: 16px;
  padding-left: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.chat-card {
  padding-left: 16px;
  margin-top: 170px;
}

.chat-title {
  font-size: 27.50px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  margin-bottom: 30px;
  margin-top: 20px;
}

.first-screen-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  min-height: 400px;
}

.first-screen-loading .loading-text {
  margin-top: 20px;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.custom-chat-input {
  font-size: 13.50px;
  font-weight: 400;
  background-color: transparent !important;
}

.custom-chat-input :deep(.v-field) {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.custom-chat-input :deep(.v-field__outline) {
  display: none !important;
}

.custom-chat-input :deep(.v-field--focused .v-field__field) {
  border-bottom: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.chat-box {
  position: relative;
  height: 140px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease;
}

.file-upload-icon {
  position: absolute;
  bottom: 16px;
  left: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-upload-icon:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.file-upload-icon img {
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.send-icon-wrapper {
  position: absolute;
  bottom: 16px;
  right: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
}

.send-icon-wrapper:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.send-icon-wrapper img {
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.chat-box.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3 !important;
  transform: scale(1.02);
}

.input-box {
  width: 564px;
  height: 134px;
}

.input-box :deep(.v-field) {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
::v-deep(.input-box .v-field__outline) {
  display: none;
}

::v-deep(.input-box .v-field--focused .v-field__field) {
  border-bottom: none !important;
  box-shadow: none !important;
}
.input-box :deep(textarea) {
  height: 100% !important;
  padding: 16px !important;
  line-height: 1.5 !important;
  resize: none;
  overflow-y: auto;
  background-color: transparent !important;
  
}

.chat-area {
  height: 100%;
}

.chat-history {
  overflow-y: auto;
  flex-grow: 1;
  max-height: 100%;
  pointer-events: auto; /* Enable pointer events for scrollbar to work */
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  /* Prevent unwanted scrolling on click */
  overscroll-behavior: contain;
  /* Prevent any programmatic scrolling except when explicitly called */
  scroll-snap-type: none;
  /* Completely disable click-to-scroll behavior */
  scroll-padding: 0;
  scroll-margin: 0;
}

/* Re-enable pointer events only on message content */
.chat-history .history-loading,
.chat-history .v-list,
.chat-history .v-list-item,
.chat-history .v-list-item-content,
.chat-history .v-list-item-content > *,
.chat-history .chat-bubble,
.chat-history .chat-bubble *,
.chat-history .profile-Logo,
.chat-history .ai-Logo,
.chat-history .studio-plot-link,
.chat-history .studio-plot-link *,
.chat-history .visualization-prompt,
.chat-history .visualization-prompt *,
.chat-history .html-content,
.chat-history .html-content *,
.chat-history .embedded-studio-container,
.chat-history .embedded-studio-container *,
.chat-history .embedded-studio-content,
.chat-history .embedded-studio-content *,
.chat-history .embedded-studio-footer,
.chat-history .embedded-studio-footer *,
.chat-history a,
.chat-history button,
.chat-history img,
.chat-history input,
.chat-history select,
.chat-history textarea,
.chat-history .v-select,
.chat-history .v-select *,
.chat-history .v-text-field,
.chat-history .v-text-field *,
.chat-history .v-btn,
.chat-history .feedback-icons,
.chat-history .feedback-icons *,
.chat-history .feedback-form-below,
.chat-history .feedback-form-below * {
  pointer-events: auto !important;
}

/* Ensure v-list background and spacing are not clickable */
.chat-history .v-list {
  background: transparent;
}

.chat-history .v-list-item {
  min-height: auto;
  padding: 0;
}

/* Make sure empty space in v-list is not clickable */
.chat-history .v-list > *:not(.v-list-item) {
  pointer-events: none;
}

/* Hide scrollbar but keep scrolling functionality */
.chat-history::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari, Edge */
  width: 0;
  height: 0;
}

.chat-history {
  -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
}

/* Default cursor for content area */
.chat-history {
  cursor: text;
}

.history-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 200px;
}

.history-loading .loading-text {
  margin-top: 16px;
  color: #666;
  font-size: 14px;
}

/* Disable all click-based scrolling */
.chat-history:active,
.chat-history:focus {
  scroll-behavior: auto !important;
}

.chat-history:focus,
.chat-history:active,
.chat-history:focus-visible {
  outline: none;
  box-shadow: none;
}

.chat-bubble {
  margin-bottom: 12px;
  border-radius: 10px;
  max-width: 75%;
  word-wrap: break-word;
  word-break: break-word;
  display: inline-block;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  cursor: text;
  pointer-events: auto;
  box-sizing: border-box;
  overflow-wrap: break-word;
}

/* Full width for chat bubble containing embedded studio */
.chat-bubble.full-width-studio {
  max-width: 100%;
  width: 100%;
  display: block;
}

/* Enable text selection for all text content in chat bubbles */
.chat-bubble * {
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

.studio-plot-link {
  margin-bottom: 12px;
}

.studio-link {
  display: inline-flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.studio-link:hover {
  background-color: #bbdefb;
  border-color: #1976d2;
  color: #1565c0;
  text-decoration: none;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
}

.studio-link:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(25, 118, 210, 0.2);
}

.studio-prompt {
  margin-bottom: 12px;
}

.studio-question {
  margin-top: 16px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  margin-bottom: 12px;
}

.studio-link-container {
  margin-top: 8px;
}

.embedded-studio-container {
  margin-top: 16px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 600px;
}

.embedded-studio-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.embedded-studio-header h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.close-studio-btn {
  min-width: auto !important;
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
}

.embedded-studio-content {
  max-height: 700px;
  overflow: auto;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.embedded-studio-footer {
  padding: 12px 16px;
  background-color: #f9f9f9;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.studio-link-footer {
  display: inline-flex;
  align-items: center;
  color: #2196f3;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s;
}

.studio-link-footer:hover {
  color: #1976d2;
  text-decoration: underline;
}

.embedded-studio-content :deep(.studio-main-layout) {
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.embedded-studio-content :deep(.studio-top-bar) {
  display: flex !important;
  position: sticky;
  top: 0;
  z-index: 100;
}

.embedded-studio-content :deep(.studio-content-wrapper) {
  display: flex !important;
  flex-direction: row !important;
  height: calc(100% - 52px);
  max-height: calc(700px - 52px);
  min-height: calc(500px - 52px);
  width: 100%;
  max-width: 100%;
}

.embedded-studio-content :deep(.studio-sidebar) {
  display: block !important;
  width: 320px !important;
  min-width: 320px !important;
  max-width: 320px !important;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  max-height: 700px;
  position: relative;
}

.embedded-studio-content :deep(.studio-sidebar > template),
.embedded-studio-content :deep(.studio-sidebar template) {
  display: contents !important;
}

.embedded-studio-content :deep(.studio-sidebar .filter-wrapper.studio-new) {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
  height: auto !important;
  min-height: 100% !important;
  max-height: 700px !important;
  padding: 16px !important;
  overflow-y: auto !important;
  box-shadow: none !important;
  border-right: none !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  z-index: 1 !important;
}

/* Ensure StudioDataLeft component is visible */
.embedded-studio-content :deep(.studio-sidebar > *) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .studio-input-form) {
  display: block !important;
  width: 100% !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Force show input form even if conditionally hidden */
.embedded-studio-content :deep(.studio-sidebar .v-col.studio-input-form) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-row) {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-col) {
  display: block !important;
  width: 100% !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .custom-field) {
  display: block !important;
  width: 100% !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar label) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-select),
.embedded-studio-content :deep(.studio-sidebar .v-text-field),
.embedded-studio-content :deep(.studio-sidebar input),
.embedded-studio-content :deep(.studio-sidebar select) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
}

/* Hide default v-select selection text in embedded studio - show only custom template */
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-select__selection) {
  display: none !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-select__selection-text) {
  display: none !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input > .v-field__input-control > .v-field__input-control-input > .v-select__selection) {
  display: none !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input > .v-field__input-control > .v-field__input-control-input > .v-select__selection-text) {
  display: none !important;
}

/* Hide all default selection elements */
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-field__input-control .v-field__input-control-input .v-select__selection),
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-field__input-control .v-field__input-control-input .v-select__selection-text),
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-field__input-control .v-field__input-control-input > .v-select__selection),
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-field__input-control .v-field__input-control-input > .v-select__selection-text) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
}

/* Show only custom selection template */
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-tooltip) {
  display: block !important;
  width: 100% !important;
  flex: 1 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-tooltip > span) {
  display: block !important;
  width: 100% !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Ensure custom selection template takes full width and is visible */
.embedded-studio-content :deep(.studio-sidebar .v-select .v-field__input .v-tooltip .text-truncate) {
  display: block !important;
  width: 100% !important;
  visibility: visible !important;
  opacity: 1 !important;
  color: inherit !important;
}

.embedded-studio-content :deep(.studio-content-area) {
  flex: 1 !important;
  width: auto !important;
  max-width: none !important;
  padding: 16px;
  overflow-y: auto;
  max-height: 700px;
}

/* Hide filters when embedded */
.embedded-studio-content :deep(.filters),
.embedded-studio-content :deep([class*="filter"]),
.embedded-studio-content :deep(.filter-drawer),
.embedded-studio-content :deep(.right-drawer) {
  display: none !important;
  visibility: hidden !important;
}

/* Ensure filter drawer doesn't affect layout when embedded */
.embedded-studio-content :deep(.studio-content-area.filter-open) {
  width: 100% !important;
  max-width: 100% !important;
}

.embedded-studio-content :deep(.studio-chart-area) {
  width: 100% !important;
  max-width: 100% !important;
}

.embedded-studio-content :deep(.studio-flex-row) {
  width: 100% !important;
  max-width: 100% !important;
}

.visualization-prompt {
  margin-bottom: 12px;
}

.visualization-question {
  margin-top: 16px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.question-text {
  margin-bottom: 12px;
  font-weight: 500;
  color: #000000;
  font-size: 14px;
}

.question-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
}

.yes-btn {
  text-transform: none;
  font-weight: 500;
}

.no-btn {
  text-transform: none;
  font-weight: 500;
}

.left {
  align-self: flex-start;
  text-align: left;
  padding: 20px;
  float: left;
}

.feedbackForm {
  width: 100%;
  text-align: left;
  padding: 20px;
  float: left;
  background-color: rgba(0, 0, 0, 0.05)
}

.feedbackButton {
  background-color: rgba(14, 61, 85, 1);
  color: white;
  border-radius: 8px;
  padding: 10px;
  float: left;
}

.feedback-close-btn {
  background-color: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15.50px;
  color: #888;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  margin-top: 8px;
}

.feedback-close-btn:hover {
  background-color: #e0e0e0;
}

.right {
  background-color: #d6f1ff;
  align-self: flex-end;
  padding: 10px;
  float: right;
  margin-left: auto;
  outline: 1px solid rgba(167, 214, 238, 1);
}


.input-wrapper {
  padding: 12px 6px 12px 12px;
}

.inner-chat-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  transition: all 0.3s ease;
}

.inner-chat-input-container.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3;
  transform: scale(1.02);
}

.inner-file-upload-icon {
  cursor: pointer;
  margin-right: 12px;
  padding: 6px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.inner-file-upload-icon:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.inner-file-upload-icon img {
  width: 18px;
  height: 18px;
  opacity: 0.6;
}

.inner-file-upload-icon.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.file-upload-icon.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.send-btn {
  position: absolute;
  bottom: 20px;
  right: 24px;
  height: 24px;
  cursor: pointer;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 25px;
}

.v-btn {
  background-color: #f5f5f5 !important;
  border: 1px solid #ddd !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13.50px;
  padding: 6px 12px !important;
  min-width: auto !important;
}

.v-btn img {
  width: 18px;
  height: 18px;
  margin-right: 6px;
  vertical-align: middle;
}

.model-select {
  padding: 16px 16px 0 30px;
  top: 0;
  left: 0;
  width: 180px;
  transform-origin: top left;
}

.send-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-right: 8px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.send-icon:hover {
  opacity: 1;
}

.divider {
  margin-top: 2px;
}

::v-deep(.v-list-item__content) {
  overflow: visible !important;
}

::v-deep(.v-field__input),
::v-deep(.dp__input),
::v-deep(.v-select .v-select__selection-text),
::v-deep(.v-text-field .v-field--no-label input),
::v-deep(.input-info .v-field__input::placeholder),
::v-deep(.v-field__input::placeholder) {
  font-size: 13.50px;
}

.profile-Logo {
  width: 32px;
  height: 32px;
  background-color: #e1dbee;
  color: #555;
  font-weight: bold;
  font-family: sans-serif;
  font-size: 13.50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 88.89px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  margin-left: 8px;
  float: right;
  margin-top: 5px;
}

.ai-Logo {
  width: 32px;
  height: 32px;
  background-color: #e1dbee;
  color: #555;
  font-weight: bold;
  font-family: sans-serif;
  font-size: 13.50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  margin-right: 8px;
  float: left;
  margin-top: 5px;
  overflow: hidden;
}

.ai-Logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.feedback-title {
  font-size: 13.50px;
  font-weight: 600;
  color: #333;
}

.feedback-input-container {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.feedback-form-below {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.active-feedback {
  background-color: #e3f2fd !important;
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.active-prompt {
  background-color: #e3f2fd !important;
  border-color: #2196f3 !important;
  color: #1976d2 !important;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2) !important;
}

.active-prompt img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%) !important;
}

.pdf-context-helper {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.50px;
  color: #1976d2;
}

.inner-chat-input-container.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.chat-box.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.feedback-icons {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
}

/* Enable pointer events for all interactive elements in chat history */
.chat-history .v-list-item,
.chat-history .v-list-item-content,
.chat-history .profile-Logo,
.chat-history .ai-Logo,
.chat-history .feedback-form-below,
.chat-history .feedback-input-container,
.chat-history .feedback-close-btn,
.chat-history .feedbackButton {
  pointer-events: auto;
}

/* Enable text selection for text content in chat history */
.chat-history .v-list-item,
.chat-history .v-list-item-content,
.chat-history .profile-Logo,
.chat-history .ai-Logo,
.chat-history .chat-bubble,
.chat-history .chat-bubble * {
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

/* HTML content styling for plots */
.html-content {
  width: 100%;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.plot-iframe {
  width: 760px;    /* Set fixed width to match backend */
  max-width: 100%; /* Ensure it doesn't overflow on smaller screens */
  height: 400px;
  border: none;
  display: block;
  background-color: white;
  border-radius: 8px;
  transition: height 0.3s ease, opacity 0.3s ease;
  opacity: 1;
}

.plot-iframe[v-show="false"] {
  opacity: 0;
}

/* Responsive iframe sizing */
@media (max-width: 768px) {
  .plot-iframe {
    width: 100%; /* Allow full width on tablets */
    height: 300px;
  }
}

@media (max-width: 480px) {
  .plot-iframe {
    width: 100%; /* Allow full width on mobile */
    height: 250px;
  }
}

/* Plot loading and error states */
.plot-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading-shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.plot-loading p {
  margin-top: 16px;
  color: #666;
  font-size: 14px;
}

.plot-loading-hint {
  margin-top: 8px !important;
  color: #999 !important;
  font-size: 12px !important;
  font-style: italic;
}

.plot-error {
  padding: 20px;
  background-color: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 8px;
  color: #c62828;
  text-align: center;
}

.plot-error p {
  margin: 0;
  font-size: 14px;
}

.plot-error a {
  color: #1976d2;
  text-decoration: underline;
  margin-left: 4px;
}

.plot-error a:hover {
  color: #1565c0;
}

</style>
